import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { CustomCheckbox, CustomInput, ToggleSwitch } from "@components/Forms";
import CustomRadio from "@components/Forms/CustomRadio";
import {
  HidePasswordIcon,
  IdentificationSettingIcon,
  ImportSettingIcon,
  ServerSettingIcon,
  ShowPasswordIcon,
  ToolSettingIcon,
  NoticeHeaderIcon,
} from "@components/Icons";
import { CustomSelect, PreferenceLanguageSelector, TimeSelector } from "@components/UI";
import Spin from "@components/UI/Spin";
import LicenseSelectionModal from "@components/UI/LicenseSelectionModal";
import { ConfirmActionModal } from "@components/UI";
import { apiBase } from "@apis/index";
import { GENESIS_SYNC_V1, isGenesisV2Enabled } from "./genesisSyncModules";
import queryKeys from "@configs/react-query/query-key";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { useClientConfig } from "@contexts/ClientConfigContext";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useMutationData } from "@hooks/useMutationData";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { useTheme } from "@hooks/useTheme";
import { formatNumberWithCommas, normalizeResponseData, unformatNumber, isValidApiUrl } from "@utils/common";
import { useEffect, useState, useMemo, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import ToastConstant from "@configs/react-toastify/snackbar";
import { TOAST, CHARACTER_LENGTH } from "@constants/index";
import CustomTextarea from "../../../components/Forms/CustomTextarea";
import LoadingSpinner from "@components/UI/LoadingSpinner";
import ImportProjectSettingModal from '../../../components/Dialogs/ImportProjectSettingModal';
import ImportHistoryModal from '../../../components/Dialogs/ImportHistoryModal';
import ClarityDynamicParamsModal from "@components/UI/ClarityDynamicParamsModal";
import GenesisSyncSection from "./GenesisSyncSection";

const MIN_VERIFICATION_TIMEOUT_SECONDS = 30;

export default function PreferencesList() {
  const { t } = useTranslation();
  const { colors } = useTheme();

  // Safely handle useClientConfig hook to prevent crashes
  const clientConfigContext = useClientConfig();
  const { updateClientConfig = () => { } } = clientConfigContext || {};

  const [isEditingMode, setIsEditingMode] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [modalSelectLicenseOpen, setModalSelectLicenseOpen] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    mailServerPassword: false,
    fossIdKey: false,
    clarityAccountPassword: false,
  });
  const [licensePolicyList, setLicensePolicyList] = useState([]);
  const [distributionScopeList, setDistributionScopeList] = useState([]);
  const { assetClassificationsList, fetchSearchInfo } = useSearchInfo();
  const [isCustomDaysSelected, setIsCustomDaysSelected] = useState(false);
  const [isImportProjectLoading, setIsImportProjectLoading] = useState(false);
  const importProjectPollingIntervalRef = useRef(null);
  const importGenesisPollingIntervalRef = useRef(null);
  const fetchSyncStatusMutateRef = useRef(null);
  const [genesisSyncStatus, setGenesisSyncStatus] = useState({
    status: "IDLE", // IDLE, RUNNING, SUCCESS, FAILED
    lastSyncAt: null,
    streams: {},
  });
  const [isGenesisSyncLoading, setIsGenesisSyncLoading] = useState(false);
  const [showImportProjectModal, setShowImportProjectModal] = useState(false);
  const [showImportHistoryModal, setShowImportHistoryModal] = useState(false);
  const [isClarityParamsModalOpen, setIsClarityParamsModalOpen] = useState(false);
  const [clarityDynamicParams, setClarityDynamicParams] = useState([]);
  const [clarityDynamicParamsDraft, setClarityDynamicParamsDraft] = useState([]);
  const [clarityAirWorkSpaceList, setClarityAirWorkSpaceList] = useState([]);
  const menuPortalTarget = typeof document !== "undefined" ? document.body : null;

  const createClarityParamRow = () => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    paramKey: "",
    paramValue: "",
    checked: true,
  });

  const parseClarityDynamicParams = (response) => {
    if (!response) return [];

    const dataPayload =
      response.data != null && typeof response.data === "object" ? response.data : response;
    const params = Array.isArray(dataPayload.params) ? dataPayload.params : [];

    return params.map((row) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
      paramKey: (row.key || "").toString(),
      paramValue: Array.isArray(row.values) ? row.values.join(",") : "",
      checked:
        row.enabled === true ||
        row.enabled === "true" ||
        row.enable === true ||
        row.enable === "true",
    }));
  };

  const buildClarityParamsPayload = (rows) =>
  ({
    params: rows
      .map((row) => ({
        key: row.paramKey.trim(),
        enabled: Boolean(row.checked),
        values: row.paramValue
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
      }))
      .filter((row) => row.key),
  });


  // Consolidated URL validation
  const urlValidations = useMemo(() => {
    const validateUrl = (value, isRequired = false) => {
      const trimmed = (value ?? "").toString().trim();
      // If empty and not required, it's valid
      if (trimmed === "" && !isRequired) {
        return true;
      }
      // If empty and required, it's invalid
      if (trimmed === "" && isRequired) {
        return false;
      }
      // If has value, must be valid URL
      return isValidApiUrl(trimmed);
    };

    return {
      isClarityApiUrlValid: validateUrl(editFormData.clarityApiUrl, false), // Optional
      isFossIdApiUrlValid: validateUrl(editFormData.fossIdApiUrl, false), // Optional
      isFossIdSiteUrlValid: validateUrl(editFormData.fossIdSiteUrl, false), // Optional
      isComponentAgentUrlValid: validateUrl(editFormData.componentAgentUrl, false), // Optional
      isClarityAirApiUrlValid: validateUrl(editFormData.clarityAirApiUrl, false), // Optional
      isClarityAirSiteUrlValid: validateUrl(editFormData.clarityAirSiteUrl, false), // Optional
    };
  }, [
    editFormData.clarityApiUrl,
    editFormData.fossIdApiUrl,
    editFormData.fossIdSiteUrl,
    editFormData.componentAgentUrl,
    editFormData.clarityAirApiUrl,
    editFormData.clarityAirSiteUrl,
  ]);

  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: "",
    description: "",
    confirmText: "",
    confirmButtonVariant: "primary",
    onConfirm: null,
  });
  const showConfirmation = (title, description, confirmText, confirmButtonVariant, onConfirm) => {
    setConfirmationModal({
      isOpen: true,
      title,
      description,
      confirmText,
      confirmButtonVariant,
      onConfirm,
    });
  };

  const closeConfirmation = () => {
    setConfirmationModal({
      isOpen: false,
      title: "",
      description: "",
      confirmText: "",
      confirmButtonVariant: "primary",
      onConfirm: null,
    });
  };

  const handleConfirmAction = () => {
    if (confirmationModal.onConfirm) {
      confirmationModal.onConfirm();
    }
    closeConfirmation();
  };

  const protocolOptions = [
    { value: "smtp", label: t("preferences.smtp") },
    { value: "smtps", label: t("preferences.smtps") },
  ];
  const encryptionOptions = [
    { value: "tls", label: t("preferences.tls") },
    { value: "ssl", label: t("preferences.ssl") },
  ];

  const booleanOptions = [
    { value: "true", label: t("Yes") },
    { value: "false", label: t("No") },
  ];

  // API
  const getLicensePolicyList = useMutationData(
    queryKeys.licensePoliciesOnly,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setLicensePolicyList(
            normalizeResponseData(response).map(({ licensePolicySeq, policyName }) => ({
              value: licensePolicySeq,
              label: policyName,
            })),
          );
        }
      },
      onError: (error) => {
        console.error("Error during get data:", error);
      },
    },
  );

  const getDistributionScopes = useMutationData(
    queryKeys.getDistributionScopes,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setDistributionScopeList(response.map((data) => ({ value: data.code, label: data.codeName })));
        }
      },
      onError: (error) => {
        console.error("Error during get data:", error);
      },
    },
  );

  const getClarityAirWorkSpaceList = useMutationData(
    queryKeys.getClarityAirWorkSpaceList,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setClarityAirWorkSpaceList(
            response.map((data) => ({
              value: data.id,
              label: data.name,
            }))
          );
        }
      },
      onError: (error) => {
        setClarityAirWorkSpaceList([]);
        console.error("Error during get data:", error);
      },
    },
  );

  useEffect(() => {
    if (isEditingMode) {
      getClarityAirWorkSpaceList.mutate();
    }
  }, [isEditingMode]);

  const [preferencesData, setPreferencesData] = useState({
    // ServerSettings
    defaultCompletionRequestDays: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "DEFAULT_COMPLETION_REQUEST_DAYS",
      codeName: "default.completion.request.days",
      codeValue: "7", // Default: 7 days
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    mailServerUrl: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "MAIL_SERVER_URL",
      codeName: "mail.server.url",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    mailServerPort: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "MAIL_SERVER_PORT",
      codeName: "mail.server.port",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    mailServerUsername: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "MAIL_SERVER_USERNAME",
      codeName: "mail.server.username",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    mailServerPassword: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "MAIL_SERVER_PASSWORD",
      codeName: "mail.server.password",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    fossIdSiteUrl: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "FOSSID_SITE_URL",
      codeName: "fossid.site.url",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    fossIdApiUrl: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "FOSSID_API_URL",
      codeName: "fossid.api.url",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    fossIdUsername: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "FOSSID_USERNAME",
      codeName: "fossid.username",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    fossIdKey: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "FOSSID_KEY",
      codeName: "fossId.key",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    clarityApiUrl: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "CLARITY_API_URL",
      codeName: "clarity.api.url",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    clarityAccountEmail: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "CLARITY_ACCOUNT_EMAIL",
      codeName: "clarity.account.email",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    clarityAccountPassword: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "CLARITY_ACCOUNT_PASSWORD",
      codeName: "clarity.account.password",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    // IdentificationSettings
    projectIdPrefix: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "PROJECT_ID_PREFIX",
      codeName: "sequence_id.project.prefix",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    projectIdSequenceLength: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "PROJECT_ID_SEQUENCE_LENGTH",
      codeName: "sequence_id.project.sequenceLength",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    verificationReportIdPrefix: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "VERIFICATION_REPORT_ID_PREFIX",
      codeName: "sequence_id.verification_report.prefix",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    verificationReportIdSequenceLength: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "VERIFICATION_REPORT_ID_SEQUENCE_LENGTH",
      codeName: "sequence_id.verification_report.sequenceLength",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    noticeInfoIdPrefix: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "NOTICE_INFO_ID_PREFIX",
      codeName: "sequence_id.notice_info.prefix",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    noticeInfoIdSequenceLength: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "NOTICE_INFO_ID_SEQUENCE_LENGTH",
      codeName: "sequence_id.notice_info.sequenceLength",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    // Tools, Locale & ETC Settings
    scanTools: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "",
      codeName: "",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    httpClientTimeout: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "HTTP_CLIENT_TIMEOUT",
      codeName: "httpclient.timeout",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    verificationTimeout: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "VERIFICATION_TIMEOUT",
      codeName: "Verification Timeout",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    localeDefault: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "LOCALE_DEFAULT",
      codeName: "locale.default",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    // sessionTimeout: {
    //   codeSeq: 0,
    //   category: "PREFERENCES",
    //   code: "SESSION_TIMEOUT",
    //   codeName: "session.timeout",
    //   codeValue: "",
    //   useFlag: null,
    //   remark: null,
    //   displayOrder: 0,
    //   createdAt: null,
    //   createrId: "system",
    //   createrName: "system",
    //   updatedAt: null,
    //   updaterId: "system",
    //   updaterName: "system",
    //   reservation1: null,
    //   reservation2: null,
    //   reservation3: null,
    //   codeI19s: [],
    // },
    cveCrawlingTime: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "CVE_CRAWLING_TIME",
      codeName: "cve.crawling.time",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    componentAgentUrl: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "COMPONENT_AGENT_URL",
      codeName: "component_agent.url",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    useVex: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "USE_VEX",
      codeName: "use.vex",
      codeValue: "N",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    useGenesisV2: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "USE_GENESIS_V2",
      codeName: "USE_GENESIS_V2",
      codeValue: "N",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    // FossID Import Settings
    ignoreExistingProject: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "IGNORE_EXISTING_PROJECT",
      codeName: "ignore.existing.project",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    amountOfProjectCount: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "AMOUNT_OF_PROJECT_COUNT",
      codeName: "amount.of.project.count",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    distributionScopeCd: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "DISTRIBUTION_SCOPE_CD",
      codeName: "distribution.scope.cd",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    licensePolicySeq: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "LICENSE_POLICY_SEQ",
      codeName: "license.policy.seq",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    assetClassificationSeq: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "ASSET_CLASSIFICATION_SEQ",
      codeName: "asset.classification.seq",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    licenseName: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "LICENSE_NAME",
      codeName: "license.name",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    licenseIdentifier: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "LICENSE_IDENTIFIER",
      codeName: "license.identifier",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    mailType: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "MAIL_TYPE",
      codeName: "",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    mailProtocol: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "MAIL_SERVER_PROTOCOL",
      codeName: "",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    mailEncryption: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "MAIL_SERVER_ENCRYPTION",
      codeName: "",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    mailAuthEnabled: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "MAIL_SERVER_AUTH_ENABLED",
      codeName: "",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    mailSslTrust: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "MAIL_SERVER_SSL_TRUST",
      codeName: "",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    mailSslProtocols: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "MAIL_SERVER_SSL_PROTOCOLS",
      codeName: "",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    mailAuthMechanisms: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "MAIL_SERVER_AUTH_MECHANISMS",
      codeName: "",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    mailStartTlsRequired: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "MAIL_START_TLS_REQUIRED",
      codeName: "",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    isSkipClarityProgress: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "IS_SKIP_CLARITY_PROGRESS",
      codeName: "",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    importFossidFlag: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "IMPORT_FOSSID_FLAG",
      codeName: "",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    clarityAirDefaultWorkspaceName: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "CLARITYAIR_DEFAULT_WORKSPACE_NAME",
      codeName: "",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    clarityAirDefaultWorkspaceId: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "CLARITYAIR_DEFAULT_WORKSPACE_ID",
      codeName: "",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    clarityAirApiUrl: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "CLARITYAIR_API_URL",
      codeName: "",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    clarityAirSiteUrl: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "CLARITYAIR_SITE_URL",
      codeName: "",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
    clarityAirPat: {
      codeSeq: 0,
      category: "PREFERENCES",
      code: "CLARITYAIR_USERNAME",
      codeName: "",
      codeValue: "",
      useFlag: null,
      remark: null,
      displayOrder: 0,
      createdAt: null,
      createrId: "system",
      createrName: "system",
      updatedAt: null,
      updaterId: "system",
      updaterName: "system",
      reservation1: null,
      reservation2: null,
      reservation3: null,
      codeI19s: [],
    },
  });

  // API
  const getPreferencesData = useMutationData(
    queryKeys.getPreferencesData,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        setPreferencesData((prev) => ({
          ...prev,
          ...response,
        }));
        // Update ClientConfigContext with fetched data to avoid duplicate API calls
        updateClientConfig(response);

        // Check if import is already in progress
        if (response?.importFossidFlag?.codeValue === "Y") {
          setIsImportProjectLoading(true);
          // Check immediately
          checkImportProjectFlag.mutate();
          // Start polling every 5 seconds
          importProjectPollingIntervalRef.current = setInterval(() => {
            checkImportProjectFlag.mutate();
          }, 5000);
        }
      },
      onError: (error) => {
        toast.error(t("preferences.loading-data-error"), {
          autoClose: 1000,
        });
      },
    },
  );

  const savePreferencesData = useMutationData(
    queryKeys.getPreferencesData,
    {
      method: "PUT",
    },
    {
      onSuccess: (response) => {
        setPreferencesData((prev) => ({
          ...prev,
          ...response,
        }));
        // Update the preference context with new data
        updateClientConfig(response);
        showSuccessToast(t("preferences.update-success"));
        setIsEditingMode(false);
        // Refresh status for the saved mode (v1 aggregate vs v2 per-stream shape)
        fetchSyncStatusMutateRef.current?.();
      },
      onError: (error) => {
        console.error("Error saving preferences:", error);
        showErrorToast(t("preferences.update-error"));
      },
    },
  );

  const getClarityScanConfig = useMutationData(
    queryKeys.clarityScanPreferences,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        setClarityDynamicParams(parseClarityDynamicParams(response));
      },
      onError: (error) => {
        console.error("Error loading Clarity scan config:", error);
      },
    },
  );

  const saveClarityScanConfig = useMutationData(
    queryKeys.clarityScanPreferences,
    {
      method: "PUT",
    },
    {
      onError: (error) => {
        console.error("Error saving Clarity scan config:", error);
      },
    },
  );

  // Handle edit mode functions
  const handleEditClick = () => {
    setIsEditingMode(true);
    setEditFormData({
      defaultCompletionRequestDays: preferencesData.defaultCompletionRequestDays?.codeValue || "7",
      mailType: preferencesData.mailType?.codeValue || "external",
      mailServerUrl: preferencesData.mailServerUrl?.codeValue || "",
      mailServerPort: preferencesData.mailServerPort?.codeValue || "",
      mailServerUsername: preferencesData.mailServerUsername?.codeValue || "",
      mailServerPassword: preferencesData.mailServerPassword?.codeValue || "",
      mailEncryption: preferencesData.mailEncryption?.codeValue || "",
      mailProtocol: preferencesData.mailProtocol?.codeValue || "",
      mailSslTrust: preferencesData.mailSslTrust?.codeValue || "",
      mailAuthEnabled: preferencesData.mailAuthEnabled?.codeValue || "",
      mailStartTlsRequired: preferencesData.mailStartTlsRequired?.codeValue || "",
      mailSslProtocols: preferencesData.mailSslProtocols?.codeValue || "",
      fossIdSiteUrl: preferencesData.fossIdSiteUrl?.codeValue || "",
      fossIdApiUrl: preferencesData.fossIdApiUrl?.codeValue || "",
      fossIdUsername: preferencesData.fossIdUsername?.codeValue || "",
      fossIdKey: preferencesData.fossIdKey?.codeValue || "",
      clarityApiUrl: preferencesData.clarityApiUrl?.codeValue || "",
      clarityAccountEmail: preferencesData.clarityAccountEmail?.codeValue || "",
      clarityAccountPassword: preferencesData.clarityAccountPassword?.codeValue || "",
      projectIdPrefix: preferencesData.projectIdPrefix?.codeValue || "",
      projectIdSequenceLength: preferencesData.projectIdSequenceLength?.codeValue || "",
      verificationReportIdPrefix: preferencesData.verificationReportIdPrefix?.codeValue || "",
      verificationReportIdSequenceLength: preferencesData.verificationReportIdSequenceLength?.codeValue || "",
      noticeInfoIdPrefix: preferencesData.noticeInfoIdPrefix?.codeValue || "",
      noticeInfoIdSequenceLength: preferencesData.noticeInfoIdSequenceLength?.codeValue || "",
      scanTools: preferencesData.scanTools?.codeValue || "",
      httpClientTimeout: preferencesData.httpClientTimeout?.codeValue || "",
      verificationTimeout: preferencesData.verificationTimeout?.codeValue || "",
      localeDefault: preferencesData.localeDefault?.codeValue || "",
      // sessionTimeout: preferencesData.sessionTimeout?.codeValue || "",
      cveCrawlingTime: preferencesData.cveCrawlingTime?.codeValue || "",
      componentAgentUrl: preferencesData.componentAgentUrl?.codeValue || "",
      usePreview: preferencesData.usePreview?.codeValue || "",
      useImportComponent: preferencesData.useImportComponent?.codeValue || "",
      useVex: preferencesData.useVex?.codeValue || "N",
      useGenesisV2: preferencesData.useGenesisV2?.codeValue || "N",
      ignoreExistingProject: preferencesData.ignoreExistingProject?.codeValue || "",
      amountOfProjectCount: preferencesData.amountOfProjectCount?.codeValue || "",
      distributionScopeCd: preferencesData.distributionScopeCd?.codeValue || "",
      licensePolicySeq: preferencesData.licensePolicySeq?.codeValue || "",
      assetClassificationSeq: preferencesData.assetClassificationSeq?.codeValue || "",
      licenseName: preferencesData.licenseName?.codeValue || "",
      licenseIdentifier: preferencesData.licenseIdentifier?.codeValue || "",
      defaultNoticeHeader: preferencesData.defaultNoticeHeader?.remark || "",
      isSkipClarityProgress: preferencesData.isSkipClarityProgress?.codeValue || "",
      clarityDynamicParams: clarityDynamicParams,
      clarityAirDefaultWorkspaceName: preferencesData.clarityAirDefaultWorkspaceName?.codeValue || "",
      clarityAirDefaultWorkspaceId: preferencesData.clarityAirDefaultWorkspaceId?.codeValue || "",
      clarityAirApiUrl: preferencesData.clarityAirApiUrl?.codeValue || "",
      clarityAirSiteUrl: preferencesData.clarityAirSiteUrl?.codeValue || "",
      clarityAirPat: preferencesData.clarityAirPat?.codeValue || "",
    });
    setClarityDynamicParamsDraft(clarityDynamicParams);
  };

  const handleCancelEdit = () => {
    setIsEditingMode(false);
    setEditFormData({});
    setValidationErrors({});
    setIsClarityParamsModalOpen(false);
    setClarityDynamicParamsDraft([]);
  };

  const handleSaveEdit = async () => {
    // Check for validation errors before saving
    const hasErrors = Object.values(validationErrors).some(error => error !== null && error !== undefined);
    if (hasErrors) {
      showErrorToast(t("preferences.please-fix-validation-errors"));
      return;
    }

    if (!preferencesData.useGenesisV2?.codeSeq) {
      showErrorToast(
        t("status.error"),
        t(
          "preferences.genesis-sync-version-missing",
          "Genesis Sync version preference is missing on the server. Ensure USE_GENESIS_V2 is configured.",
        ),
      );
      return;
    }

    // Check if required sequence length fields are empty
    const requiredFields = [
      { field: 'verificationReportIdSequenceLength', label: t("preferences.verification-report-id-seq-length") },
      { field: 'noticeInfoIdSequenceLength', label: t("preferences.notice-info-id-seq-length") },
      { field: 'projectIdSequenceLength', label: t("preferences.project-id-seq-length") },
      { field: 'defaultCompletionRequestDays', label: t("project.completion-request-date") },
    ];

    const emptyFields = requiredFields.filter(({ field }) => !editFormData[field] || editFormData[field].trim() === '');

    if (emptyFields.length > 0) {
      const fieldNames = emptyFields.map(({ label }) => label).join(', ');
      ToastConstant(TOAST.error, t("common.error"), `${fieldNames} ${t("toast.validation.required")}`);
      return;
    }

    const verificationTimeoutValue = parseInt(editFormData.verificationTimeout, 10);
    if (!editFormData.verificationTimeout
      || Number.isNaN(verificationTimeoutValue)
      || verificationTimeoutValue < MIN_VERIFICATION_TIMEOUT_SECONDS
    ) {
      setValidationErrors((prev) => ({
        ...prev,
        verificationTimeout: t("preferences.minimum-verification-timeout-is-30-seconds"),
      }));
      showErrorToast(t("preferences.please-fix-validation-errors"));
      return;
    }

    const {
      importFossidFlag,
      ...restPreferencesData
    } = preferencesData;

    // Prepare the data for the API call
    const updateData = {
      ...restPreferencesData,
      defaultCompletionRequestDays: { ...preferencesData.defaultCompletionRequestDays, codeValue: editFormData.defaultCompletionRequestDays },
      mailType: { ...preferencesData.mailType, codeValue: editFormData.mailType },
      mailServerUrl: { ...preferencesData.mailServerUrl, codeValue: editFormData.mailServerUrl },
      mailServerPort: { ...preferencesData.mailServerPort, codeValue: editFormData.mailServerPort },
      mailServerUsername: { ...preferencesData.mailServerUsername, codeValue: editFormData.mailServerUsername },
      mailServerPassword: { ...preferencesData.mailServerPassword, codeValue: editFormData.mailServerPassword },
      mailEncryption: { ...preferencesData.mailEncryption, codeValue: editFormData.mailEncryption },
      mailProtocol: { ...preferencesData.mailProtocol, codeValue: editFormData.mailProtocol },
      mailSslTrust: { ...preferencesData.mailSslTrust, codeValue: editFormData.mailSslTrust },
      mailAuthEnabled: { ...preferencesData.mailAuthEnabled, codeValue: editFormData.mailAuthEnabled },
      mailStartTlsRequired: { ...preferencesData.mailStartTlsRequired, codeValue: editFormData.mailStartTlsRequired },
      mailSslProtocols: { ...preferencesData.mailSslProtocols, codeValue: editFormData.mailSslProtocols },
      fossIdSiteUrl: { ...preferencesData.fossIdSiteUrl, codeValue: editFormData.fossIdSiteUrl },
      fossIdApiUrl: { ...preferencesData.fossIdApiUrl, codeValue: editFormData.fossIdApiUrl },
      fossIdUsername: { ...preferencesData.fossIdUsername, codeValue: editFormData.fossIdUsername },
      fossIdKey: { ...preferencesData.fossIdKey, codeValue: editFormData.fossIdKey },
      clarityAccountEmail: { ...preferencesData.clarityAccountEmail, codeValue: editFormData.clarityAccountEmail },
      clarityAccountPassword: {
        ...preferencesData.clarityAccountPassword,
        codeValue: editFormData.clarityAccountPassword,
      },
      projectIdPrefix: { ...preferencesData.projectIdPrefix, codeValue: editFormData.projectIdPrefix },
      projectIdSequenceLength: {
        ...preferencesData.projectIdSequenceLength,
        codeValue: editFormData.projectIdSequenceLength,
      },
      verificationReportIdPrefix: {
        ...preferencesData.verificationReportIdPrefix,
        codeValue: editFormData.verificationReportIdPrefix,
      },
      verificationReportIdSequenceLength: {
        ...preferencesData.verificationReportIdSequenceLength,
        codeValue: editFormData.verificationReportIdSequenceLength,
      },
      noticeInfoIdPrefix: { ...preferencesData.noticeInfoIdPrefix, codeValue: editFormData.noticeInfoIdPrefix },
      noticeInfoIdSequenceLength: {
        ...preferencesData.noticeInfoIdSequenceLength,
        codeValue: editFormData.noticeInfoIdSequenceLength,
      },
      scanTools: { ...preferencesData.scanTools, codeValue: editFormData.scanTools },
      httpClientTimeout: { ...preferencesData.httpClientTimeout, codeValue: editFormData.httpClientTimeout },
      verificationTimeout: { ...preferencesData.verificationTimeout, codeValue: editFormData.verificationTimeout },
      localeDefault: { ...preferencesData.localeDefault, codeValue: editFormData.localeDefault },
      // sessionTimeout: { ...preferencesData.sessionTimeout, codeValue: editFormData.sessionTimeout },
      cveCrawlingTime: { ...preferencesData.cveCrawlingTime, codeValue: editFormData.cveCrawlingTime },
      componentAgentUrl: {
        ...preferencesData.componentAgentUrl,
        codeValue: editFormData.componentAgentUrl,
      },
      usePreview: { ...preferencesData.usePreview, codeValue: editFormData.usePreview },
      useImportComponent: { ...preferencesData.useImportComponent, codeValue: editFormData.useImportComponent },
      useVex: { ...preferencesData.useVex, codeValue: editFormData.useVex || "N" },
      useGenesisV2: {
        ...preferencesData.useGenesisV2,
        codeValue: editFormData.useGenesisV2 || "N",
      },
      ignoreExistingProject: {
        ...preferencesData.ignoreExistingProject,
        codeValue: editFormData.ignoreExistingProject,
      },
      amountOfProjectCount: { ...preferencesData.amountOfProjectCount, codeValue: editFormData.amountOfProjectCount },
      distributionScopeCd: { ...preferencesData.distributionScopeCd, codeValue: editFormData.distributionScopeCd },
      licensePolicySeq: { ...preferencesData.licensePolicySeq, codeValue: editFormData.licensePolicySeq },
      assetClassificationSeq: {
        ...preferencesData.assetClassificationSeq,
        codeValue: editFormData.assetClassificationSeq,
      },
      licenseName: { ...preferencesData.licenseName, codeValue: editFormData.licenseName },
      licenseIdentifier: { ...preferencesData.licenseIdentifier, codeValue: editFormData.licenseIdentifier },
      defaultNoticeHeader: { ...preferencesData.defaultNoticeHeader, remark: editFormData.defaultNoticeHeader },
      isSkipClarityProgress: { ...preferencesData.isSkipClarityProgress, codeValue: editFormData.isSkipClarityProgress },
      clarityApiUrl: {
        ...preferencesData.clarityApiUrl,
        codeValue: editFormData.clarityApiUrl,
      },
      // clarityAirDefaultWorkspaceName: { ...preferencesData.clarityAirDefaultWorkspaceName, codeValue: (editFormData.clarityAirPat && editFormData.clarityAirApiUrl) ? editFormData.clarityAirDefaultWorkspaceName : "" },
      clarityAirDefaultWorkspaceName: { ...preferencesData.clarityAirDefaultWorkspaceName, codeValue: editFormData.clarityAirDefaultWorkspaceName },
      clarityAirDefaultWorkspaceId: { ...preferencesData.clarityAirDefaultWorkspaceId, codeValue: editFormData.clarityAirDefaultWorkspaceId },
      clarityAirApiUrl: { ...preferencesData.clarityAirApiUrl, codeValue: editFormData.clarityAirApiUrl },
      clarityAirSiteUrl: { ...preferencesData.clarityAirSiteUrl, codeValue: editFormData.clarityAirSiteUrl },
      clarityAirPat: { ...preferencesData.clarityAirPat, codeValue: editFormData.clarityAirPat },
    };

    const normalizedRows = Array.isArray(editFormData.clarityDynamicParams)
      ? editFormData.clarityDynamicParams
      : [];

    const clarityPayload = buildClarityParamsPayload(normalizedRows);
    if (clarityPayload.params.length > 0) {
      try {
        const clarityPutResponse = await saveClarityScanConfig.mutateAsync(clarityPayload);
        const syncedRows = parseClarityDynamicParams(clarityPutResponse);
        if (syncedRows.length > 0) {
          setClarityDynamicParams(syncedRows);
          setEditFormData((prev) => ({
            ...prev,
            clarityDynamicParams: syncedRows,
          }));
        } else {
          setClarityDynamicParams(normalizedRows);
        }
      } catch (error) {
        showErrorToast(t("preferences.update-error"));
        return;
      }
    }

    // Call the API to update the preferences
    savePreferencesData.mutate(updateData);
    setIsCustomDaysSelected(false)
  };

  const handleInputChange = (field, value) => {
    const numberFields = [
      "projectIdSequenceLength",
      "verificationReportIdSequenceLength",
      "noticeInfoIdSequenceLength",
    ];

    // Validate number fields
    if (numberFields.includes(field)) {
      // Clear previous error first
      setValidationErrors((prev) => ({
        ...prev,
        [field]: null,
      }));

      // Skip validation for empty value
      if (value === "") {
        setEditFormData((prev) => ({
          ...prev,
          [field]: value,
        }));
        return;
      }

      // Filter out non-numeric characters
      const numericValue = value.replace(/[^0-9]/g, "");

      // If no numeric characters remain after filtering, set to empty
      if (numericValue === "") {
        setEditFormData((prev) => ({
          ...prev,
          [field]: "",
        }));
        return;
      }

      // Update the displayed value with only numeric characters
      setEditFormData((prev) => ({
        ...prev,
        [field]: numericValue,
      }));

      // Check if value is at least 4
      const numValue = parseInt(numericValue, 10);
      if (numValue < 4) {
        setValidationErrors((prev) => ({
          ...prev,
          [field]: t("preferences.minimum-value-is-4"),
        }));
        return;
      }

      return;
    }

    if (field === "verificationTimeout") {
      setValidationErrors((prev) => ({
        ...prev,
        verificationTimeout: null,
      }));

      if (value === "") {
        setEditFormData((prev) => ({
          ...prev,
          verificationTimeout: value,
        }));
        return;
      }

      const numericValue = value.replace(/[^0-9]/g, "");
      if (numericValue === "") {
        setEditFormData((prev) => ({
          ...prev,
          verificationTimeout: "",
        }));
        return;
      }

      setEditFormData((prev) => ({
        ...prev,
        verificationTimeout: numericValue,
      }));

      const numValue = parseInt(numericValue, 10);
      if (numValue < MIN_VERIFICATION_TIMEOUT_SECONDS) {
        setValidationErrors((prev) => ({
          ...prev,
          verificationTimeout: t("preferences.minimum-verification-timeout-is-30-seconds"),
        }));
      }
      return;
    }

    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const openClarityParamsModal = async () => {
    let currentRows = Array.isArray(editFormData.clarityDynamicParams)
      ? editFormData.clarityDynamicParams
      : [];

    try {
      const response = await getClarityScanConfig.mutateAsync();
      const latestRows = parseClarityDynamicParams(response);
      currentRows = latestRows;
      setClarityDynamicParams(latestRows);
      setEditFormData((prev) => ({
        ...prev,
        clarityDynamicParams: latestRows,
      }));
    } catch (error) {
      console.error("Error loading Clarity params from button click:", error);
    }

    setClarityDynamicParamsDraft(Array.isArray(currentRows) ? currentRows : []);
    setIsClarityParamsModalOpen(true);
  };

  const handleAddClarityParamRow = () => {
    setClarityDynamicParamsDraft((prev) => [...prev, createClarityParamRow()]);
  };

  const handleUpdateClarityParamRow = (rowId, field, value) => {
    setClarityDynamicParamsDraft((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, [field]: value } : row)),
    );
  };

  const handleDeleteClarityParamRow = (rowId) => {
    setClarityDynamicParamsDraft((prev) => prev.filter((row) => row.id !== rowId));
  };

  const handleToggleClarityParamRow = (rowId) => {
    setClarityDynamicParamsDraft((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, checked: !row.checked } : row)),
    );
  };

  const handleApplyClarityParams = async () => {
    const normalizedRows = clarityDynamicParamsDraft
      .map((row) => ({
        ...row,
        paramKey: row.paramKey.trim(),
        paramValue: row.paramValue.trim(),
      }))
      .filter((row) => row.paramKey || row.paramValue);

    try {
      const clarityPutResponse = await saveClarityScanConfig.mutateAsync(
        buildClarityParamsPayload(normalizedRows),
      );
      const syncedRows = parseClarityDynamicParams(clarityPutResponse);
      if (syncedRows.length > 0) {
        setClarityDynamicParams(syncedRows);
        setClarityDynamicParamsDraft(syncedRows);
        setEditFormData((prev) => ({
          ...prev,
          clarityDynamicParams: syncedRows,
        }));
      } else {
        setClarityDynamicParams(normalizedRows);
        setClarityDynamicParamsDraft(normalizedRows);
        setEditFormData((prev) => ({
          ...prev,
          clarityDynamicParams: normalizedRows,
        }));
      }
      setIsClarityParamsModalOpen(false);
      showSuccessToast(
        t("preferences.clarity-scan-params-saved", "Clarity scan parameters saved"),
      );
    } catch (error) {
      showErrorToast(t("preferences.update-error"));
    }
  };

  // Helper function to validate and filter scan tools
  const validateScanTools = (scanToolsString) => {
    const allowedTools = ["clarity", "fossid", "sbom", "clarityair"];
    if (!scanToolsString) return "";

    const tools = scanToolsString.split(",").filter((t) => t.trim());
    const validTools = tools.filter((tool) => allowedTools.includes(tool));
    return validTools.join(",");
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // Fetch data on component mount
  useEffect(() => {
    getPreferencesData.mutate();
    getLicensePolicyList.mutate();
    getDistributionScopes.mutate();
    getClarityAirWorkSpaceList.mutate();
    fetchSearchInfo();
  }, []);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (importProjectPollingIntervalRef.current) {
        clearInterval(importProjectPollingIntervalRef.current);
        importProjectPollingIntervalRef.current = null;
      }
    };
  }, []);

  // Listen for fossid_import_complete event from MainLayout to stop polling
  useEffect(() => {
    const handleFossidImportComplete = () => {
      // Stop polling when import is complete
      if (importProjectPollingIntervalRef.current) {
        clearInterval(importProjectPollingIntervalRef.current);
        importProjectPollingIntervalRef.current = null;
      }
      setIsImportProjectLoading(false);
    };

    window.addEventListener('fossid_import_complete', handleFossidImportComplete);

    return () => {
      window.removeEventListener('fossid_import_complete', handleFossidImportComplete);
    };
  }, []);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (importGenesisPollingIntervalRef.current) {
        clearInterval(importGenesisPollingIntervalRef.current);
        importGenesisPollingIntervalRef.current = null;
      }
    };
  }, []);

  // Fetch initial sync status on mount
  useEffect(() => {
    // Only fetch if fetchSyncStatus is defined
    if (fetchSyncStatus && typeof fetchSyncStatus.mutate === 'function') {
      fetchSyncStatus.mutate();
    }
  }, []);

  const checkImportProjectFlag = useMutationData(
    "import_project_flag",
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        // Check if import is complete
        // codeValue: "Y" means import is in progress, "N" means import is complete
        const isComplete = response && response.codeValue === "N";

        if (isComplete) {
          // Stop polling
          if (importProjectPollingIntervalRef.current) {
            clearInterval(importProjectPollingIntervalRef.current);
            importProjectPollingIntervalRef.current = null;
          }
          setIsImportProjectLoading(false);
          // showSuccessToast(t("preferences.import-projects-success"));
        }
      },
      onError: (error) => {
        // Stop polling on error
        if (importProjectPollingIntervalRef.current) {
          clearInterval(importProjectPollingIntervalRef.current);
          importProjectPollingIntervalRef.current = null;
        }
        setIsImportProjectLoading(false);
        // showErrorToast(t("preferences.import-projects-error"));
      },
    },
  );

  const importProjects = useMutationData(
    "import_project_from_fossid",
    {
      method: "POST",
    },
    {
      onSuccess: (response) => {
        // Start polling for import status
        setIsImportProjectLoading(true);
        // Check immediately
        checkImportProjectFlag.mutate();
        // Start polling every 5 seconds
        importProjectPollingIntervalRef.current = setInterval(() => {
          checkImportProjectFlag.mutate();
        }, 5000);
      },
      onError: (error) => {
        const status = error?.response?.status;
        //409 – business conflict
        if (status === 409) {
          showErrorToast(
            `${t("preferences.import-projects-error")}`,
            `${error.response.data}`
          );
          setIsImportProjectLoading(false);
          return;
        }
        console.error("Error during import projects:", error);
        showErrorToast(t("preferences.import-projects-error"));
        setIsImportProjectLoading(false);
      },
    },
  );

  // Genesis Sync mutations and queries
  const isGenesisV2 = isGenesisV2Enabled(preferencesData?.useGenesisV2?.codeValue);

  const startGenesisStatusPolling = () => {
    if (importGenesisPollingIntervalRef.current) return;
    const statusInterval = setInterval(() => {
      fetchSyncStatusMutateRef.current?.();
    }, 4000);
    importGenesisPollingIntervalRef.current = statusInterval;
  };

  const stopGenesisStatusPolling = () => {
    if (importGenesisPollingIntervalRef.current) {
      clearInterval(importGenesisPollingIntervalRef.current);
      importGenesisPollingIntervalRef.current = null;
    }
  };

  const fetchSyncStatus = useMutationData(
    GENESIS_SYNC_V1.statusPath,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (response) {
          setGenesisSyncStatus({
            status: response.status || "IDLE",
            lastSyncAt: response.lastSyncAt || null,
            streams: response.streams || {},
          });

          if (response.status === "RUNNING") {
            setIsGenesisSyncLoading(true);
            startGenesisStatusPolling();
          } else {
            stopGenesisStatusPolling();
            setIsGenesisSyncLoading(false);
          }
        }
      },
      onError: (error) => {
        console.error("Error fetching sync status:", error);
      },
    },
  );

  fetchSyncStatusMutateRef.current = fetchSyncStatus.mutate;

  useEffect(() => {
    const handleGenesisImportComplete = () => {
      fetchSyncStatus.mutate();
    };

    window.addEventListener("genesis_import_complete", handleGenesisImportComplete);

    return () => {
      window.removeEventListener("genesis_import_complete", handleGenesisImportComplete);
    };
  }, [fetchSyncStatus]);

  const getGenesisSyncErrorMessage = (error, fallbackKey) =>
    error?.response?.data ||
    error?.message ||
    t(fallbackKey, "Failed to start Genesis sync");

  const startIncrementalSync = useMutationData(
    GENESIS_SYNC_V1.startPath,
    {
      method: "POST",
    },
    {
      onSuccess: () => {
        setIsGenesisSyncLoading(true);
        setGenesisSyncStatus((prev) => ({ ...prev, status: "RUNNING" }));
        startGenesisStatusPolling();
        showSuccessToast(t("preferences.genesis-sync-started", "Genesis sync started"));
      },
      onError: (error) => {
        setIsGenesisSyncLoading(false);
        setGenesisSyncStatus((prev) => ({ ...prev, status: "FAILED" }));
        showErrorToast(
          t("status.error"),
          getGenesisSyncErrorMessage(error, "preferences.genesis-sync-start-error"),
        );
      },
    },
  );

  const startFullSync = useMutationData(
    GENESIS_SYNC_V1.fullPath,
    {
      method: "POST",
    },
    {
      onSuccess: () => {
        setIsGenesisSyncLoading(true);
        setGenesisSyncStatus((prev) => ({ ...prev, status: "RUNNING" }));
        startGenesisStatusPolling();
        showSuccessToast(t("preferences.genesis-full-sync-started", "Genesis full sync started"));
      },
      onError: (error) => {
        setIsGenesisSyncLoading(false);
        setGenesisSyncStatus((prev) => ({ ...prev, status: "FAILED" }));
        showErrorToast(
          t("status.error"),
          getGenesisSyncErrorMessage(error, "preferences.genesis-sync-start-error"),
        );
      },
    },
  );

  const markStreamRunning = (stream) => {
    setIsGenesisSyncLoading(true);
    setGenesisSyncStatus((prev) => ({
      ...prev,
      status: "RUNNING",
      streams: {
        ...(prev.streams || {}),
        [stream]: {
          ...(prev.streams?.[stream] || {}),
          status: "RUNNING",
        },
      },
    }));
    startGenesisStatusPolling();
  };

  const startStreamIncrementalSync = useMutation({
    mutationFn: (module) =>
      apiBase({
        url: module?.startPath || queryKeys.genesisSyncStreamStart(module?.statusStreamKey),
        method: "POST",
      }),
    onSuccess: (_data, module) => {
      markStreamRunning(module?.statusStreamKey);
      showSuccessToast(t("preferences.genesis-sync-started", "Genesis sync started"));
    },
    onError: (error) => {
      showErrorToast(
        t("status.error"),
        getGenesisSyncErrorMessage(error, "preferences.genesis-sync-start-error"),
      );
      fetchSyncStatus.mutate();
    },
  });

  const startStreamFullSync = useMutation({
    mutationFn: (module) =>
      apiBase({
        url: module?.fullPath || queryKeys.genesisSyncStreamFull(module?.statusStreamKey),
        method: "POST",
      }),
    onSuccess: (_data, module) => {
      markStreamRunning(module?.statusStreamKey);
      showSuccessToast(t("preferences.genesis-full-sync-started", "Genesis full sync started"));
    },
    onError: (error) => {
      showErrorToast(
        t("status.error"),
        getGenesisSyncErrorMessage(error, "preferences.genesis-sync-start-error"),
      );
      fetchSyncStatus.mutate();
    },
  });

  const handleStartIncrementalSync = () => {
    if (isGenesisV2) return;
    showConfirmation(
      t("preferences.genesis-sync-incremental-title", "Sync Now"),
      t(
        "preferences.genesis-sync-incremental-description",
        "This will continue the synchronization from the last completed point and import any new or updated components and licenses from Genesis. Continue?",
      ),
      t("common.start-button", "Start"),
      "primary",
      () => {
        startIncrementalSync.mutate();
      },
    );
  };

  const handleStartFullSync = () => {
    if (isGenesisV2) return;
    showConfirmation(
      t("preferences.genesis-sync-full-title", "Start Full Sync"),
      t(
        "preferences.genesis-sync-full-description",
        "This will restart the synchronization and re-import all components and licenses from Genesis from the beginning. This action may take a long time. Are you sure?",
      ),
      t("common.start-button", "Start"),
      "primary",
      () => {
        startFullSync.mutate();
      },
    );
  };

  const handleV1GenesisSyncSelect = (option) => {
    if (!option || isGenesisSyncLoading || isGenesisV2) return;
    if (option.value === "incremental") {
      handleStartIncrementalSync();
    } else if (option.value === "full") {
      handleStartFullSync();
    }
  };

  const handleV2GenesisSyncSelect = (module, option) => {
    if (!option || !module || isGenesisV2 === false) return;
    if (!module.enabled) {
      showErrorToast(
        t("status.error"),
        t("preferences.genesis-sync-coming-soon", "Coming soon"),
      );
      return;
    }

    const moduleLabel = t(module.labelKey, module.labelDefault);

    if (option.value === "incremental") {
      showConfirmation(
        t("preferences.genesis-sync-incremental-title", "Sync Now"),
        t(
          "preferences.genesis-sync-stream-incremental-description",
          "This will continue synchronization for {{module}} from the last completed point. Continue?",
          { module: moduleLabel },
        ),
        t("common.start-button", "Start"),
        "primary",
        () => {
          startStreamIncrementalSync.mutate(module);
        },
      );
    } else if (option.value === "full") {
      showConfirmation(
        t("preferences.genesis-sync-full-title", "Start Full Sync"),
        t(
          "preferences.genesis-sync-stream-full-description",
          "This will wipe and re-import {{module}} from Genesis from the beginning. This action may take a long time. Are you sure?",
          { module: moduleLabel },
        ),
        t("common.start-button", "Start"),
        "primary",
        () => {
          startStreamFullSync.mutate(module);
        },
      );
    }
  };

  // Validation function to check if all required fields for Import Projects are filled
  const isImportProjectsDisabled = () => {
    const requiredFields = [
      preferencesData.ignoreExistingProject?.codeValue,
      preferencesData.amountOfProjectCount?.codeValue,
      preferencesData.distributionScopeCd?.codeValue,
      preferencesData.assetClassificationSeq?.codeValue,
      preferencesData.licensePolicySeq?.codeValue,
      preferencesData.licenseName?.codeValue
    ];

    return requiredFields.some(field => !field || field === '' || field === null || field === undefined);
  };

  const handleImportProject = (payload) => {
    showConfirmation(
      t("preferences.import-project-confirm-title", "Import Projects"),
      t("preferences.import-project-confirm-description", "Are you sure you want to import projects from FossID? This action may take some time."),
      t("preferences.import-project-confirm-button", "Import Projects"),
      "primary",
      () => {
        importProjects.mutate(payload);
      }
    );
  };

  const handleDataFromModal = (payload) => {
    handleImportProject(payload);
  };

  const serverSettingSection = () => {
    return (
      <div className={`mb-4 rounded-2xl p-4 ${colors.bgPrimary} ${colors.borderPrimary}`}>
        {/* Content with Left and Right Sections */}
        <div className="flex flex-row h-full gap-6">
          {/* Left Side - Icon, Title, Description */}
          <div className="w-1/5 flex-shrink-0 h-full p-4">
            <div className="flex flex-col items-start gap-3 h-full justify-start relative">
              <ServerSettingIcon width={64} height={64} className={colors.textSecondary} />
              <div>
                <h3 className={`font-medium ${colors.textPrimary} uppercase`}>
                  {t("preferences.server-settings-title", "SERVER SETTINGS")}
                </h3>
                <p className={`text-xs ${colors.textSecondary} mt-1`}>
                  {t(
                    "preferences.server-settings-description",
                    "Configures FossID and Clarity server connections to external services/APIs.",
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Vertical Divider */}
          <div className={`block border-l -my-4 ${colors.dividerDashed}`}></div>

          {/* Right Side - Server Information */}
          <div className="w-4/5 h-full">
            <div className="space-y-4">
              {/* Row 1 - Mail Server */}
              <div className="space-y-4">
                {/* Mail Server Title */}
                <div className="w-full">
                  <label className={`block text-sm font-medium ${colors.textPrimary} uppercase`}>
                    {t("preferences.mail-server-title")}
                  </label>
                </div>

                {/* Mail Type Selection */}
                <div className="flex items-center gap-4">
                  <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                    {t("preferences.mail-type", "Mail Type")}
                  </label>
                  {isEditingMode ? (
                    <div className="flex items-center gap-6">
                      <CustomRadio
                        name="mailType"
                        value="internal"
                        checked={editFormData.mailType == "internal"}
                        onChange={(e) => handleInputChange("mailType", e.target.value)}
                        label={t("preferences.internal")}
                      />
                      <CustomRadio
                        name="mailType"
                        value="external"
                        checked={editFormData.mailType == "external"}
                        onChange={(e) => handleInputChange("mailType", e.target.value)}
                        label={t("preferences.external")}
                      />
                    </div>
                  ) : (
                    <div
                      className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${preferencesData.mailType?.codeValue === "internal"
                        ? `${colors.bgSelectedOption} ${colors.textActive}`
                        : `${colors.bgMailTypeExternal} ${colors.textCompletionRequestDate}`
                        }`}
                    >
                      {preferencesData.mailType?.codeValue === "internal"
                        ? t("preferences.internal")
                        : t("preferences.external")}
                    </div>
                  )}
                </div>

                {/* Mail Server Content - 2x2 Grid Layout */}
                <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                  {/* Row 1, Column 1 - Mail Server Url */}
                  <div className="flex items-center gap-4">
                    <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                      {t("preferences.mail-server-url", "Mail Server Url")}
                    </label>
                    {isEditingMode ? (
                      <CustomInput
                        type="text"
                        value={editFormData.mailServerUrl || ""}
                        onChange={(e) => handleInputChange("mailServerUrl", e.target.value)}
                        placeholder=""
                        className="flex-1"
                      />
                    ) : (
                      <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                        {preferencesData.mailServerUrl?.codeValue || "---"}
                      </p>
                    )}
                  </div>

                  {/* Row 1, Column 2 - Mail Server Port */}
                  <div className="flex items-center gap-4">
                    <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                      {t("preferences.mail-server-port", "Mail Server Port")}
                    </label>
                    {isEditingMode ? (
                      <CustomInput
                        type="text"
                        value={editFormData.mailServerPort || ""}
                        onChange={(e) => handleInputChange("mailServerPort", e.target.value)}
                        placeholder=""
                        className="flex-1"
                      />
                    ) : (
                      <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                        {preferencesData.mailServerPort?.codeValue || "---"}
                      </p>
                    )}
                  </div>

                  {/* Row 2, Column 1 - Mail Server Username */}
                  <div className="flex items-center gap-4">
                    <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                      {t("preferences.mail-server-username", "Mail Server Username")}
                    </label>
                    {isEditingMode ? (
                      <CustomInput
                        type="text"
                        value={editFormData.mailServerUsername || ""}
                        onChange={(e) => handleInputChange("mailServerUsername", e.target.value)}
                        placeholder=""
                        className="flex-1"
                      />
                    ) : (
                      <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                        {preferencesData.mailServerUsername?.codeValue || "---"}
                      </p>
                    )}
                  </div>

                  {/* Row 2, Column 2 - Mail Server Password */}
                  <div className="flex items-center gap-4">
                    <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                      {t("preferences.mail-server-password", "Mail Server Password")}
                    </label>
                    {isEditingMode ? (
                      <div className="flex-1 relative">
                        <CustomInput
                          type={showPasswords.mailServerPassword ? "text" : "password"}
                          value={editFormData.mailServerPassword || ""}
                          onChange={(e) => handleInputChange("mailServerPassword", e.target.value)}
                          placeholder=""
                          className="flex-1 pr-10"
                        />
                        <button
                          type="button"
                          className={`absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer ${colors.textSecondary} ${colors.textHoverActive} p-1`}
                          onClick={() => togglePasswordVisibility("mailServerPassword")}
                        >
                          {showPasswords.mailServerPassword ? (
                            <HidePasswordIcon className="h-4 w-4" />
                          ) : (
                            <ShowPasswordIcon className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 flex-1">
                        <p className={`text-sm ${colors.textPrimary} h-10 flex items-center break-all`}>
                          {preferencesData.mailServerPassword?.codeValue
                            ? showPasswords.mailServerPassword
                              ? preferencesData.mailServerPassword.codeValue
                              : "************"
                            : "---"}
                        </p>
                        {preferencesData.mailServerPassword?.codeValue && (
                          <button
                            type="button"
                            className={`cursor-pointer p-1 ${colors.textHoverActive}`}
                            onClick={() => togglePasswordVisibility("mailServerPassword")}
                          >
                            {showPasswords.mailServerPassword ? (
                              <HidePasswordIcon className={`h-4 w-4 ${colors.textActive}`} />
                            ) : (
                              <ShowPasswordIcon className={`h-4 w-4 ${colors.textActive}`} />
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Additional fields for Internal mail type */}
                  {editFormData.mailType === "internal" && isEditingMode && (
                    <>
                      {/* Row 3, Column 1 - Encryption */}
                      <div className="flex items-center gap-4">
                        <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                          {t("preferences.encryption", "Encryption")}
                        </label>
                        <CustomSelect
                          options={encryptionOptions}
                          value={encryptionOptions.find((opt) => opt.value === editFormData.mailEncryption) || null}
                          onChange={(option) => handleInputChange("mailEncryption", option?.value || "")}
                          placeholder={t("common.please-select")}
                          className="flex-1"
                        />
                      </div>

                      {/* Row 3, Column 2 - Protocol */}
                      <div className="flex items-center gap-4">
                        <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                          {t("preferences.protocol", "Protocol")}
                        </label>
                        <CustomSelect
                          options={protocolOptions}
                          value={protocolOptions.find((opt) => opt.value === editFormData.mailProtocol) || null}
                          onChange={(option) => handleInputChange("mailProtocol", option?.value || "")}
                          placeholder={t("common.please-select")}
                          className="flex-1"
                        />
                      </div>

                      {/* Row 4, Column 1 - SSL Trust */}
                      <div className="flex items-center gap-4">
                        <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                          {t("preferences.ssl-trust", "SSL Trust")}
                        </label>
                        <CustomInput
                          type="text"
                          value={editFormData.mailSslTrust || ""}
                          onChange={(e) => handleInputChange("mailSslTrust", e.target.value)}
                          placeholder={t("preferences.ssl-trust")}
                          className="flex-1"
                        />
                      </div>

                      {/* Row 4, Column 2 - Enable Authentication */}
                      <div className="flex items-center gap-4">
                        <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                          {t("preferences.enable-authentication", "Enable Authentication")}
                        </label>
                        <CustomSelect
                          options={booleanOptions}
                          value={booleanOptions.find((opt) => opt.value === editFormData.mailAuthEnabled) || null}
                          onChange={(option) => handleInputChange("mailAuthEnabled", option?.value || "")}
                          placeholder={t("common.please-select")}
                          className="flex-1"
                        />
                      </div>

                      {/* Row 5, Column 1 - Start TLS Required */}
                      <div className="flex items-center gap-4">
                        <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                          {t("preferences.starttls-required", "Start TLS Required")}
                        </label>
                        <CustomSelect
                          options={booleanOptions}
                          value={booleanOptions.find((opt) => opt.value === editFormData.mailStartTlsRequired) || null}
                          onChange={(option) => handleInputChange("mailStartTlsRequired", option?.value || "")}
                          placeholder={t("common.please-select")}
                          className="flex-1"
                        />
                      </div>

                      {/* Row 5, Column 2 - SSL Protocols */}
                      <div className="flex items-center gap-4">
                        <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                          {t("preferences.ssl-protocols", "SSL Protocols")}
                        </label>
                        <CustomInput
                          type="text"
                          value={editFormData.mailSslProtocols || ""}
                          onChange={(e) => handleInputChange("mailSslProtocols", e.target.value)}
                          placeholder={t("preferences.ssl-protocols")}
                          className="flex-1"
                        />
                      </div>
                    </>
                  )}

                  {/* Display additional fields for Internal mail type in view mode */}
                  {preferencesData.mailType?.codeValue === "internal" && !isEditingMode && (
                    <>
                      {/* Row 3, Column 1 - Encryption */}
                      <div className="flex items-center gap-4">
                        <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                          {t("preferences.encryption", "Encryption")}
                        </label>
                        <p
                          className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1 uppercase`}
                        >
                          {preferencesData.mailEncryption?.codeValue || "---"}
                        </p>
                      </div>

                      {/* Row 3, Column 2 - Protocol */}
                      <div className="flex items-center gap-4">
                        <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                          {t("preferences.protocol", "Protocol")}
                        </label>
                        <p
                          className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1 uppercase`}
                        >
                          {preferencesData.mailProtocol?.codeValue || "---"}
                        </p>
                      </div>

                      {/* Row 4, Column 1 - SSL Trust */}
                      <div className="flex items-center gap-4">
                        <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                          {t("preferences.ssl-trust", "SSL Trust")}
                        </label>
                        <p
                          className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}
                        >
                          {preferencesData.mailSslTrust?.codeValue || "---"}
                        </p>
                      </div>

                      {/* Row 4, Column 2 - Enable Authentication */}
                      <div className="flex items-center gap-4">
                        <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                          {t("preferences.enable-authentication", "Enable Authentication")}
                        </label>
                        <p
                          className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}
                        >
                          {preferencesData.mailAuthEnabled?.codeValue === "true" ? t("Yes") : t("No") || "---"}
                        </p>
                      </div>

                      {/* Row 5, Column 1 - Start TLS Required */}
                      <div className="flex items-center gap-4">
                        <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                          {t("preferences.starttls-required", "Start TLS Required")}
                        </label>
                        <p
                          className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}
                        >
                          {preferencesData.mailStartTlsRequired?.codeValue === "true" ? t("Yes") : t("No") || "---"}
                        </p>
                      </div>

                      {/* Row 5, Column 2 - SSL Protocols */}
                      <div className="flex items-center gap-4">
                        <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                          {t("preferences.ssl-protocols", "SSL Protocols")}
                        </label>
                        <p
                          className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}
                        >
                          {preferencesData.mailSslProtocols?.codeValue || "---"}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Horizontal Divider */}
              <div className={`border-t ${colors.dividerDashed} -ml-6 -mr-4`}></div>

              {/* Row 2 - FOSSID Server */}
              <div className="space-y-4">
                {/* FOSSID Server Title */}
                <div className="w-full">
                  <label className={`block text-sm font-medium ${colors.textPrimary} uppercase`}>
                    {t("preferences.foss-id-server-title")}
                  </label>
                </div>

                {/* FOSSID Server Content - 2x2 Grid Layout */}
                <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                  {/* Row 1, Column 1 - FossId Site Url */}
                  <div className="flex items-center gap-4">
                    <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                      {t("preferences.fossid-site-url", "FossId Site Url")}
                    </label>
                    {isEditingMode ? (
                      <div className="flex-1">
                        <CustomInput
                          type="text"
                          value={editFormData.fossIdSiteUrl || ""}
                          onChange={(e) => handleInputChange("fossIdSiteUrl", e.target.value)}
                          placeholder=""
                          className={`flex-1 ${!urlValidations.isFossIdSiteUrlValid ? colors.borderDangerOutLineButton : ""}`}
                        />
                        {!urlValidations.isFossIdSiteUrlValid && (
                          <p className={`mt-1 text-xs ${colors.textDanger}`}>
                            {t("preferences.api-url-error", "Please enter a valid URL")}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                        {preferencesData.fossIdSiteUrl?.codeValue || "---"}
                      </p>
                    )}
                  </div>

                  {/* Row 1, Column 2 */}

                  <div className="flex items-center gap-4">
                    <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                      {t("preferences.fossid-username", "FossId Username")}
                    </label>
                    {isEditingMode ? (
                      <CustomInput
                        type="text"
                        value={editFormData.fossIdUsername || ""}
                        onChange={(e) => handleInputChange("fossIdUsername", e.target.value)}
                        placeholder=""
                        className="flex-1"
                      />
                    ) : (
                      <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                        {preferencesData.fossIdUsername?.codeValue || "---"}
                      </p>
                    )}
                  </div>

                  {/* Row 2, Column 1 */}
                  <div className="flex items-center gap-4">
                    <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                      {t("preferences.fossid-api-url", "FossId API Url")}
                    </label>
                    {isEditingMode ? (
                      <div className="flex-1">
                        <CustomInput
                          type="text"
                          value={editFormData.fossIdApiUrl || ""}
                          onChange={(e) => handleInputChange("fossIdApiUrl", e.target.value)}
                          placeholder=""
                          className={`flex-1 ${!urlValidations.isFossIdApiUrlValid ? colors.borderDangerOutLineButton : ""}`}
                        />
                        {!urlValidations.isFossIdApiUrlValid && (
                          <p className={`mt-1 text-xs ${colors.textDanger}`}>
                            {t("preferences.api-url-error", "Please enter a valid URL")}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                        {preferencesData.fossIdApiUrl?.codeValue || "---"}
                      </p>
                    )}
                  </div>

                  {/* Row 2, Column 2 - FossId API Key */}
                  <div className="flex items-center gap-4">
                    <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                      {t("preferences.fossid-api-key")}
                    </label>
                    {isEditingMode ? (
                      <div className="flex-1 relative">
                        <CustomInput
                          type={showPasswords.fossIdKey ? "text" : "password"}
                          value={editFormData.fossIdKey || ""}
                          onChange={(e) => handleInputChange("fossIdKey", e.target.value)}
                          placeholder=""
                          className="flex-1 pr-10"
                        />
                        <button
                          type="button"
                          className={`absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer ${colors.textSecondary} ${colors.textHoverActive} p-1`}
                          onClick={() => togglePasswordVisibility("fossIdKey")}
                        >
                          {showPasswords.fossIdKey ? (
                            <HidePasswordIcon className="h-4 w-4" />
                          ) : (
                            <ShowPasswordIcon className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 flex-1">
                        <p className={`text-sm ${colors.textPrimary} h-10 flex items-center break-all`}>
                          {preferencesData.fossIdKey?.codeValue
                            ? showPasswords.fossIdKey
                              ? preferencesData.fossIdKey.codeValue
                              : "************"
                            : "---"}
                        </p>
                        {preferencesData.fossIdKey?.codeValue && (
                          <button
                            type="button"
                            className={`cursor-pointer p-1 ${colors.textHoverActive}`}
                            onClick={() => togglePasswordVisibility("fossIdKey")}
                          >
                            {showPasswords.fossIdKey ? (
                              <HidePasswordIcon className={`h-4 w-4 ${colors.textActive}`} />
                            ) : (
                              <ShowPasswordIcon className={`h-4 w-4 ${colors.textActive}`} />
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Horizontal Divider */}
              <div className={`border-t ${colors.dividerDashed} -ml-6 -mr-4`}></div>

              {/* Row 3 - Clarity Server */}
              <div className="space-y-4">
                {/* Clarity Server Title */}
                <div className="w-full">
                  <label className={`block text-sm font-medium ${colors.textPrimary} uppercase`}>
                    {t("preferences.clarity-server-title")}
                  </label>
                </div>

                {/* Clarity Server Content - 2x2 Grid Layout */}
                <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                  {/* Row 1, Column 1 - Clarity API Url */}
                  <div className="flex items-center gap-4">
                    <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                      {t("preferences.clarity-api-url", "Clarity API Url")}
                    </label>
                    {isEditingMode ? (
                      <div className="flex-1">
                        <CustomInput
                          type="text"
                          value={editFormData.clarityApiUrl || ""}
                          onChange={(e) => handleInputChange("clarityApiUrl", e.target.value)}
                          placeholder=""
                          className={`flex-1 ${!urlValidations.isClarityApiUrlValid ? colors.borderDangerOutLineButton : ""}`}
                        />
                        {!urlValidations.isClarityApiUrlValid && (
                          <p className={`mt-1 text-xs ${colors.textDanger}`}>
                            {t("preferences.api-url-error", "Please enter a valid URL")}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                        {preferencesData.clarityApiUrl?.codeValue || "---"}
                      </p>
                    )}
                  </div>

                  {/* Row 1, Column 2 - Clarity Username */}
                  <div className="flex items-center gap-4">
                    <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                      {t("preferences.clarity-password", "Clarity Password")}
                    </label>
                    {isEditingMode ? (
                      <div className="flex-1 relative">
                        <CustomInput
                          type={showPasswords.clarityAccountPassword ? "text" : "password"}
                          value={editFormData.clarityAccountPassword || ""}
                          onChange={(e) => handleInputChange("clarityAccountPassword", e.target.value)}
                          placeholder=""
                          className="flex-1 pr-10"
                        />
                        <button
                          type="button"
                          className={`absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer ${colors.textSecondary} ${colors.textHoverActive} p-1`}
                          onClick={() => togglePasswordVisibility("clarityAccountPassword")}
                        >
                          {showPasswords.clarityAccountPassword ? (
                            <HidePasswordIcon className="h-4 w-4" />
                          ) : (
                            <ShowPasswordIcon className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 flex-1">
                        <p className={`text-sm ${colors.textPrimary} h-10 flex items-center break-all`}>
                          {preferencesData.clarityAccountPassword?.codeValue
                            ? showPasswords.clarityAccountPassword
                              ? preferencesData.clarityAccountPassword.codeValue
                              : "************"
                            : "---"}
                        </p>
                        {preferencesData.clarityAccountPassword?.codeValue && (
                          <button
                            type="button"
                            className={`cursor-pointer p-1 ${colors.textHoverActive}`}
                            onClick={() => togglePasswordVisibility("clarityAccountPassword")}
                          >
                            {showPasswords.clarityAccountPassword ? (
                              <HidePasswordIcon className={`h-4 w-4 ${colors.textActive}`} />
                            ) : (
                              <ShowPasswordIcon className={`h-4 w-4 ${colors.textActive}`} />
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Row 2, Column 1 - Clarity Password */}
                  <div className="flex items-center gap-4">
                    <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                      {t("preferences.clarity-username", "Clarity Username")}
                    </label>
                    {isEditingMode ? (
                      <CustomInput
                        type="text"
                        value={editFormData.clarityAccountEmail || ""}
                        onChange={(e) => handleInputChange("clarityAccountEmail", e.target.value)}
                        placeholder=""
                        className="flex-1"
                      />
                    ) : (
                      <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                        {preferencesData.clarityAccountEmail?.codeValue || "---"}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                      {t("preferences.skip-clarity-workflow")}
                    </label>
                    {isEditingMode ? (
                      <ToggleSwitch
                        checked={editFormData.isSkipClarityProgress === "Y"}
                        onChange={() => handleInputChange("isSkipClarityProgress", editFormData.isSkipClarityProgress === "Y" ? "N" : "Y")}
                      />
                    ) : (
                      <div className="flex items-center min-h-[2.5rem] flex-1">
                        <span
                          className={`px-2 py-1 rounded-md text-sm font-bold uppercase ${preferencesData.isSkipClarityProgress?.codeValue == "Y"
                            ? colors.badgeYes
                            : colors.badgeNo
                            }`}
                        >
                          {preferencesData.isSkipClarityProgress?.codeValue == "Y" ? t("common.yes") : t("common.no")}
                        </span>
                      </div>
                    )}
                  </div>
                  {(
                    <div className="col-span-2 flex items-center gap-4">
                      <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                        {t("preferences.clarity-dynamic-params", "Clarity Dynamic Params")}
                      </label>
                      <div className="flex items-center gap-3">
                        <OutlineButton onClick={openClarityParamsModal}>
                          {t("preferences.configure-params", "Configure Params")}
                        </OutlineButton>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Horizontal Divider */}
              <div className={`border-t ${colors.dividerDashed} -ml-6 -mr-4`}></div>

              {/* Row 4 - CLARITYAIR Server */}
              <div className="space-y-4">
                {/* CLARITYAIR Server Title */}
                <div className="w-full">
                  <label className={`block text-sm font-medium ${colors.textPrimary} uppercase`}>
                    {t("preferences.clarity-air-server")}
                  </label>
                </div>
                {/* CLARITYAIR Server Content - 2x2 Grid Layout */}
                <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                  {/* Row 1, Column 1 - ClarityAIR Default Workspace Id */}
                  <div className="flex items-center gap-4">
                    <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                      {t("preferences.clarity-air-default-workspace-id")}
                    </label>
                    {isEditingMode ? (
                      <div className="flex-1">
                        <CustomInput
                          type="number"
                          value={editFormData.clarityAirDefaultWorkspaceId || ""}
                          onChange={(e) => handleInputChange("clarityAirDefaultWorkspaceId", e.target.value)}
                          placeholder=""
                          className={`flex-1`}
                        />
                      </div>
                    ) : (
                      <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                        {preferencesData.clarityAirDefaultWorkspaceId?.codeValue || "---"}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-4">
                    <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                      {t("preferences.clarity-air-default-workspace-name")}
                    </label>
                    {isEditingMode ? (
                      <div className="flex-1">
                        <CustomSelect
                          options={clarityAirWorkSpaceList}
                          value={
                            clarityAirWorkSpaceList.find(
                              (opt) => opt.label === editFormData.clarityAirDefaultWorkspaceName
                            ) || null
                          }

                          onChange={(option) =>
                            handleInputChange("clarityAirDefaultWorkspaceName", (option?.label))
                          }
                          placeholder={t("common.please-select")}
                          className="flex-1"
                        />
                      </div>
                    ) : (
                      <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                        {preferencesData.clarityAirDefaultWorkspaceName?.codeValue || "---"}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-4">
                    <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                      {t("preferences.clarity-air-site-url")}
                    </label>
                    {isEditingMode ? (
                      <div className="flex-1">
                        <CustomInput
                          type="text"
                          value={editFormData.clarityAirSiteUrl || ""}
                          onChange={(e) => handleInputChange("clarityAirSiteUrl", e.target.value)}
                          placeholder=""
                          className={`flex-1 ${!urlValidations.isClarityAirSiteUrlValid ? colors.borderDangerOutLineButton : ""}`}
                        />
                        {!urlValidations.isClarityAirSiteUrlValid && (
                          <p className={`mt-1 text-xs ${colors.textDanger}`}>
                            {t("preferences.api-url-error", "Please enter a valid URL")}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                        {preferencesData.clarityAirSiteUrl?.codeValue || "---"}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-4">
                    <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                      {t("preferences.clarity-air-api-url")}
                    </label>
                    {isEditingMode ? (
                      <div className="flex-1">
                        <CustomInput
                          type="text"
                          value={editFormData.clarityAirApiUrl || ""}
                          onChange={(e) => handleInputChange("clarityAirApiUrl", e.target.value)}
                          placeholder=""
                          className={`flex-1 ${!urlValidations.isClarityAirApiUrlValid ? colors.borderDangerOutLineButton : ""}`}
                        />
                        {!urlValidations.isClarityAirApiUrlValid && (
                          <p className={`mt-1 text-xs ${colors.textDanger}`}>
                            {t("preferences.api-url-error", "Please enter a valid URL")}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                        {preferencesData.clarityAirApiUrl?.codeValue || "---"}
                      </p>
                    )}
                  </div>

                  <div className="flex gap-4">
                    <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                      {t("preferences.clarity-air-personal-access-token")}
                    </label>

                    {isEditingMode ? (
                      <div className="flex-1 relative">
                        <CustomInput
                          type={showPasswords.clarityAirPat ? "text" : "password"}
                          value={editFormData.clarityAirPat || ""}
                          onChange={(e) => handleInputChange("clarityAirPat", e.target.value)}
                          className="flex-1"
                        />
                        <button
                          type="button"
                          className={`absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer ${colors.textSecondary} ${colors.textHoverActive} p-1`}
                          onClick={() => togglePasswordVisibility("clarityAirPat")}
                        >
                          {showPasswords.clarityAirPat ? (
                            <HidePasswordIcon className="h-4 w-4" />
                          ) : (
                            <ShowPasswordIcon className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2 items-start">
                        <p
                          title={
                            showPasswords.clarityAirPat
                              ? preferencesData.clarityAirPat?.codeValue
                              : undefined
                          }
                          className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex-1 line-clamp-2 break-all`}
                        >
                          {preferencesData.clarityAirPat?.codeValue
                            ? showPasswords.clarityAirPat
                              ? preferencesData.clarityAirPat.codeValue
                              : "************"
                            : "---"}
                        </p>
                        {preferencesData.clarityAirPat?.codeValue && (
                          <button
                            type="button"
                            className={`cursor-pointer p-1 ${colors.textHoverActive}`}
                            onClick={() => togglePasswordVisibility("clarityAirPat")}
                          >
                            {showPasswords.clarityAirPat ? (
                              <HidePasswordIcon className={`h-4 w-4 ${colors.textActive}`} />
                            ) : (
                              <ShowPasswordIcon className={`h-4 w-4 ${colors.textActive}`} />
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const identificationSettingSection = () => {
    return (
      <div className={`mb-4 rounded-2xl p-4 ${colors.bgPrimary} ${colors.borderPrimary}`}>
        {/* Content with Left and Right Sections */}
        <div className="flex flex-row h-full gap-6">
          {/* Left Side - Icon, Title, Description */}
          <div className="w-1/5 flex-shrink-0 h-full p-4">
            <div className="flex flex-col items-start gap-3 h-full justify-start relative">
              <IdentificationSettingIcon size={48} className={colors.textSecondary} />
              <div>
                <h3 className={`font-medium ${colors.textPrimary} uppercase`}>
                  {t("preferences.identification-settings-title", "IDENTIFICATION SETTINGS")}
                </h3>
                <p className={`text-xs ${colors.textSecondary} mt-1`}>
                  {t(
                    "preferences.identification-settings-description",
                    "Defines the ID formats used for various entities in the system.",
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Vertical Divider */}
          <div className={`block border-l -my-4 ${colors.dividerDashed}`}></div>

          {/* Right Side - ID Formats */}
          <div className="w-4/5 h-full">
            <div className="space-y-4">
              {/* PROJECT ID */}
              <div className="space-y-4">
                {/* Project ID Title */}
                <div className="w-full">
                  <label className={`block text-sm font-medium ${colors.textPrimary} uppercase`}>
                    {t("preferences.project-id-title", "PROJECT ID")}
                  </label>
                </div>

                {/* Project ID Content - 2x2 Grid Layout */}
                <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                  {/* Row 1, Column 1 - Project ID Prefix */}
                  <div className="flex items-start gap-4">
                    <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0 pt-2`}>
                      {t("preferences.project-id-prefix", "Project ID Prefix")}
                    </label>
                    {isEditingMode ? (
                      <div className="flex-1">
                        <CustomInput
                          type="text"
                          value={editFormData.projectIdPrefix || ""}
                          onChange={(e) => handleInputChange("projectIdPrefix", e.target.value)}
                          placeholder="KO_LAAL_"
                          className="w-full"
                        />
                        <div className="h-4 mt-1"></div>
                      </div>
                    ) : (
                      <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                        {preferencesData.projectIdPrefix?.codeValue || "---"}
                      </p>
                    )}
                  </div>

                  {/* Row 1, Column 2 - Project ID Sequence Length */}
                  <div className="flex items-start gap-4">
                    <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0 pt-2`}>
                      {t("preferences.project-id-seq-length", "Project ID Seq. Length")}
                    </label>
                    {isEditingMode ? (
                      <div className="flex-1">
                        <CustomInput
                          type="number"
                          value={editFormData.projectIdSequenceLength || ""}
                          onChange={(e) => handleInputChange("projectIdSequenceLength", e.target.value)}
                          placeholder="4"
                          className={`w-full ${validationErrors.projectIdSequenceLength ? colors.borderDangerOutLineButton : ''}`}
                        />
                        <div className="h-4 mt-1">
                          {validationErrors.projectIdSequenceLength && (
                            <p className={`${colors.textDanger} text-xs`}>
                              {validationErrors.projectIdSequenceLength}
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                        {preferencesData.projectIdSequenceLength?.codeValue || "---"}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Horizontal Divider */}
              <div className={`border-t ${colors.dividerDashed} -ml-6 -mr-4`}></div>

              {/* VERIFICATION REPORT ID */}
              <div className="space-y-4">
                {/* Verification Report ID Title */}
                <div className="w-full">
                  <label className={`block text-sm font-medium ${colors.textPrimary} uppercase`}>
                    {t("preferences.verification-report-id-title", "VERIFICATION REPORT ID")}
                  </label>
                </div>

                {/* Verification Report ID Content - 2x2 Grid Layout */}
                <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                  {/* Row 1, Column 1 - Verif Report ID Prefix */}
                  <div className="flex items-start gap-4">
                    <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0 pt-2`}>
                      {t("preferences.verification-report-id-prefix", "Verif. Report ID Prefix")}
                    </label>
                    {isEditingMode ? (
                      <div className="flex-1">
                        <CustomInput
                          type="text"
                          value={editFormData.verificationReportIdPrefix || ""}
                          onChange={(e) => handleInputChange("verificationReportIdPrefix", e.target.value)}
                          placeholder="KS_VAAR_"
                          className="w-full"
                        />
                        <div className="h-4 mt-1"></div>
                      </div>
                    ) : (
                      <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                        {preferencesData.verificationReportIdPrefix?.codeValue || "---"}
                      </p>
                    )}
                  </div>

                  {/* Row 1, Column 2 - Verif Report ID Sequence Length */}
                  <div className="flex items-start gap-4">
                    <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0 pt-2`}>
                      {t("preferences.verification-report-id-seq-length", "Verif. Report ID Seq. Length")}
                    </label>
                    {isEditingMode ? (
                      <div className="flex-1">
                        <CustomInput
                          type="number"
                          value={editFormData.verificationReportIdSequenceLength || ""}
                          onChange={(e) => handleInputChange("verificationReportIdSequenceLength", e.target.value)}
                          placeholder="4"
                          className={`w-full ${validationErrors.verificationReportIdSequenceLength ? colors.borderDangerOutLineButton : ''}`}
                        />
                        <div className="h-4 mt-1">
                          {validationErrors.verificationReportIdSequenceLength && (
                            <p className={`${colors.textDanger} text-xs`}>
                              {validationErrors.verificationReportIdSequenceLength}
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                        {preferencesData.verificationReportIdSequenceLength?.codeValue || "---"}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Horizontal Divider */}
              <div className={`border-t ${colors.dividerDashed} -ml-6 -mr-4`}></div>

              {/* NOTICE INFORMATION ID */}
              <div className="space-y-4">
                {/* Notice Information ID Title */}
                <div className="w-full">
                  <label className={`block text-sm font-medium ${colors.textPrimary} uppercase`}>
                    {t("preferences.notice-information-id-title", "NOTICE INFORMATION ID")}
                  </label>
                </div>

                {/* Notice Information ID Content - 2x2 Grid Layout */}
                <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                  {/* Row 1, Column 1 - Notice Info ID Prefix */}
                  <div className="flex items-start gap-4">
                    <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0 pt-2`}>
                      {t("preferences.notice-info-id-prefix", "Notice Info ID Prefix")}
                    </label>
                    {isEditingMode ? (
                      <div className="flex-1">
                        <CustomInput
                          type="text"
                          value={editFormData.noticeInfoIdPrefix || ""}
                          onChange={(e) => handleInputChange("noticeInfoIdPrefix", e.target.value)}
                          placeholder="KS_NAAI_"
                          className="w-full"
                        />
                        <div className="h-4 mt-1"></div>
                      </div>
                    ) : (
                      <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                        {preferencesData.noticeInfoIdPrefix?.codeValue || "---"}
                      </p>
                    )}
                  </div>

                  {/* Row 1, Column 2 - Notice Info ID Sequence Length */}
                  <div className="flex items-start gap-4">
                    <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0 pt-2`}>
                      {t("preferences.notice-info-id-seq-length", "Notice Info ID Seq. Length")}
                    </label>
                    {isEditingMode ? (
                      <div className="flex-1">
                        <CustomInput
                          type="number"
                          value={editFormData.noticeInfoIdSequenceLength || ""}
                          onChange={(e) => handleInputChange("noticeInfoIdSequenceLength", e.target.value)}
                          placeholder="4"
                          className={`w-full ${validationErrors.noticeInfoIdSequenceLength ? colors.borderDangerOutLineButton : ''}`}
                        />
                        <div className="h-5 mt-1">
                          {validationErrors.noticeInfoIdSequenceLength && (
                            <p className={`${colors.textDanger} text-xs`}>
                              {validationErrors.noticeInfoIdSequenceLength}
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                        {preferencesData.noticeInfoIdSequenceLength?.codeValue || "---"}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const toolsLocaleEtcSection = () => {
    return (
      <div className={`mb-4 rounded-2xl p-4 ${colors.bgPrimary} ${colors.borderPrimary}`}>
        {/* Content with Left and Right Sections */}
        <div className="flex flex-row h-full gap-6">
          {/* Left Side - Icon, Title, Description */}
          <div className="w-1/5 flex-shrink-0 h-full p-4">
            <div className="flex flex-col items-start gap-3 h-full justify-start relative">
              <ToolSettingIcon size={48} className={colors.textSecondary} />
              <div>
                <h3 className={`font-medium ${colors.textPrimary} uppercase`}>
                  {t("preferences.tools-locale-etc-settings-title", "TOOLS, LOCALE & ETC SETTINGS")}
                </h3>
                <p className={`text-xs ${colors.textSecondary} mt-1`}>
                  {t(
                    "preferences.tools-locale-etc-settings-description",
                    "Defines tool settings, localization, and key system parameters.",
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Vertical Divider */}
          <div className={`block border-l -my-4 ${colors.dividerDashed}`}></div>

          {/* Right Side - Settings */}
          <div className="w-4/5 h-full">
            <div className="space-y-4">
              {/* Row 1 */}
              <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                {/* Usage Verif Tool */}
                <div className="col-span-2">
                  <div className="flex flex-row items-center gap-4">
                    <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                      {t("preferences.usage-verif-tool", "Usage Verif. Tools")}
                    </label>
                    {isEditingMode ? (
                      <div className="flex flex-wrap items-center gap-6 flex-1 min-w-0">
                        <label className="flex items-center gap-2 flex-shrink-0">
                          <CustomCheckbox
                            disabled={true}
                            checked={editFormData.scanTools?.includes("clarity") || false}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const currentTools = editFormData.scanTools
                                ? editFormData.scanTools.split(",").filter((t) => t.trim())
                                : [];
                              if (checked) {
                                if (!currentTools.includes("clarity")) {
                                  currentTools.push("clarity");
                                }
                              } else {
                                const index = currentTools.indexOf("clarity");
                                if (index > -1) {
                                  currentTools.splice(index, 1);
                                }
                              }
                              handleInputChange("scanTools", validateScanTools(currentTools.join(",")));
                            }}
                          />
                          <span className={`text-sm ${colors.textPrimary}`}>Clarity</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer flex-shrink-0">
                          <CustomCheckbox
                            checked={editFormData.scanTools?.includes("fossid") || false}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const currentTools = editFormData.scanTools
                                ? editFormData.scanTools.split(",").filter((t) => t.trim())
                                : [];
                              if (checked) {
                                if (!currentTools.includes("fossid")) {
                                  currentTools.push("fossid");
                                }
                              } else {
                                const index = currentTools.indexOf("fossid");
                                if (index > -1) {
                                  currentTools.splice(index, 1);
                                }
                              }
                              handleInputChange("scanTools", validateScanTools(currentTools.join(",")));
                            }}
                          />
                          <span className={`text-sm ${colors.textPrimary}`}>FossID</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer flex-shrink-0">
                          <CustomCheckbox
                            checked={editFormData.scanTools?.includes("sbom") || false}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const currentTools = editFormData.scanTools
                                ? editFormData.scanTools.split(",").filter((t) => t.trim())
                                : [];
                              if (checked) {
                                if (!currentTools.includes("sbom")) {
                                  currentTools.push("sbom");
                                }
                              } else {
                                const index = currentTools.indexOf("sbom");
                                if (index > -1) {
                                  currentTools.splice(index, 1);
                                }
                              }
                              handleInputChange("scanTools", validateScanTools(currentTools.join(",")));
                            }}
                          />
                          <span className={`text-sm ${colors.textPrimary}`}>SBOM</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer flex-shrink-0">
                          <CustomCheckbox
                            checked={editFormData.scanTools?.includes("clarityair") || false}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const currentTools = editFormData.scanTools
                                ? editFormData.scanTools.split(",").filter((t) => t.trim())
                                : [];
                              if (checked) {
                                if (!currentTools.includes("clarityair")) {
                                  currentTools.push("clarityair");
                                }
                              } else {
                                const index = currentTools.indexOf("clarityair");
                                if (index > -1) {
                                  currentTools.splice(index, 1);
                                }
                              }
                              handleInputChange("scanTools", validateScanTools(currentTools.join(",")));
                            }}
                          />
                          <span className={`text-sm ${colors.textPrimary}`}>{t("common.clarityair")}</span>
                        </label>
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2 flex-1 min-h-[2.5rem]">
                        {preferencesData.scanTools?.codeValue ? (
                          validateScanTools(preferencesData.scanTools.codeValue)
                            .split(",")
                            .filter((t) => t.trim())
                            .map((tool, index) => (
                              <span
                                key={index}
                                className={`px-4 py-1 rounded-full text-sm font-medium ${colors.bgDivider} ${colors.textPrimary} border ${colors.borderPrimary} flex-shrink-0`}
                              >
                                {tool.trim()}
                              </span>
                            ))
                        ) : (
                          <span className={`text-sm ${colors.textPrimary} h-10 flex items-center`}>---</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* HTTP Client Timeout */}
                <div className="flex items-center gap-4">
                  <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                    {t("preferences.http-client-timeout", "HTTP Client Timeout (seconds)")}
                  </label>
                  {isEditingMode ? (
                    <CustomInput
                      type="text"
                      value={formatNumberWithCommas(editFormData?.httpClientTimeout)}
                      onChange={(e) => {
                        const rawValue = unformatNumber(e.target.value);

                        if (rawValue === "" || !isNaN(Number(rawValue))) {
                          handleInputChange("httpClientTimeout", rawValue);
                        } else {
                          handleInputChange("httpClientTimeout", "");
                        }
                      }}
                      placeholder="3600"
                      className="flex-1"
                    />
                  ) : (
                    <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                      {formatNumberWithCommas(preferencesData.httpClientTimeout?.codeValue) || "---"}
                    </p>
                  )}
                </div>

                {/* Default Locale */}
                <div className="flex items-center gap-4">
                  <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                    {t("preferences.default-locale", "Default Locale")}
                  </label>
                  {isEditingMode ? (
                    <PreferenceLanguageSelector
                      value={editFormData.localeDefault || ""}
                      onChange={(value) => handleInputChange("localeDefault", value)}
                      placeholder="Select language"
                      className="flex-1"
                      size="md"
                    />
                  ) : (
                    <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                      {preferencesData.localeDefault?.codeValue === "en"
                        ? t("language.english")
                        : preferencesData.localeDefault?.codeValue === "ko"
                          ? t("language.korean")
                          : preferencesData.localeDefault?.codeValue === "ja"
                            ? t("language.japanese")
                            : preferencesData.localeDefault?.codeValue || "---"}
                    </p>
                  )}
                </div>

                {/* Session Timeout */}
                {/* <div className="flex items-center gap-4">
                  <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                    {t("preferences.session-timeout", "Session Timeout (seconds)")}
                  </label>
                  {isEditingMode ? (
                    <CustomInput
                      type="text"
                      value={formatNumberWithCommas(editFormData?.sessionTimeout)}
                      onChange={(e) => {
                        const rawValue = unformatNumber(e.target.value);

                        if (rawValue === "" || !isNaN(Number(rawValue))) {
                          handleInputChange("sessionTimeout", rawValue);
                        } else {
                          handleInputChange("sessionTimeout", "");
                        }
                      }}
                      placeholder="7200"
                      className="flex-1"
                    />
                  ) : (
                    <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                      {formatNumberWithCommas(preferencesData.sessionTimeout?.codeValue) || "---"}
                    </p>
                  )}
                </div> */}

                {/* CVE Crawling Time */}
                <div className="flex items-center gap-4">
                  <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                    {t("preferences.cve-crawling-time", "CVE Crawling Time")}
                  </label>
                  {isEditingMode ? (
                    <TimeSelector
                      value={editFormData.cveCrawlingTime || ""}
                      onChange={(value) => handleInputChange("cveCrawlingTime", value)}
                      placeholder="00:00"
                      className="w-31"
                      size="md"
                    />
                  ) : (
                    <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                      {preferencesData.cveCrawlingTime?.codeValue || "---"}
                    </p>
                  )}
                </div>
                {/* Default Completion Request Offset */}
                <div className="flex items-center gap-4">
                  <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                    {t("preferences.default-completion-request-offset", "Default Completion Request Offset")}
                  </label>

                  {isEditingMode ? (
                    <div className="flex items-start gap-3 flex-wrap">

                      {/* --- Constants & Helpers --- */}
                      {(() => {
                        const PRESET_DAYS = ["7", "15", "30"];
                        const currentValue = editFormData.defaultCompletionRequestDays;
                        const isPreset = isCustomDaysSelected || !PRESET_DAYS.includes(currentValue);
                        const selectedValue = PRESET_DAYS.includes(currentValue)
                          ? {
                            value: currentValue,
                            label: `${currentValue} ${t("preferences.days")}`,
                          }
                          : { value: "other", label: t("preferences.other", "Other") };

                        const handleSelectChange = (opt) => {
                          const selected = opt?.value;

                          if (selected === "other") {
                            const fallback = isPreset ? currentValue : "";
                            handleInputChange("defaultCompletionRequestDays", fallback);
                            setIsCustomDaysSelected(true);
                          } else {
                            handleInputChange("defaultCompletionRequestDays", selected || "7");
                            setIsCustomDaysSelected(false);
                          }
                        };

                        const handleCustomDaysChange = (e) => {
                          let day = e.target.value;
                          if (day !== "0" && day.startsWith("0")) {
                            day = day.replace(/^0+/, "") || "0";
                          }
                          if (day.length > 5 || !/^\d*$/.test(day) || (day !== "" && Number(day) <= 0)) {
                            return;
                          }
                          handleInputChange("defaultCompletionRequestDays", day);
                          setIsCustomDaysSelected(true);
                        };

                        return (
                          <>
                            {/* Select preset days */}
                            <CustomSelect
                              options={[
                                { value: "other", label: t("preferences.other", "Other") },
                                ...PRESET_DAYS.map((day) => ({
                                  value: day,
                                  label: `${day} ${t("preferences.days", "Days")}`,
                                })),
                              ]}
                              value={selectedValue}
                              onChange={handleSelectChange}
                              placeholder={t("common.please-select")}
                              className="!w-35"
                              isSearchable={false}
                            />

                            {/* Custom input (only when 'other') */}
                            {isPreset && (
                              <div className="flex items-center gap-2 flex-wrap">
                                <CustomInput
                                  type="number"
                                  min="1"
                                  placeholder={t("preferences.enter-days")}
                                  value={currentValue}
                                  onKeyDown={(e) => {
                                    if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault();
                                  }}
                                  onChange={handleCustomDaysChange}
                                  className="!w-28"
                                />
                                <span className={`text-sm ${colors.textSecondary}`}>
                                  {t("preferences.day-label", "Day(s)")}
                                </span>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    // View Mode
                    <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                      {preferencesData.defaultCompletionRequestDays?.codeValue ? (
                        <>
                          {preferencesData.defaultCompletionRequestDays.codeValue}
                          {" "}
                          {t("preferences.day-label", "Day(s)")}
                        </>
                      ) : (
                        "---"
                      )}
                    </p>
                  )}
                </div>
              </div>

              {/* Row 2 */}
              <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                {/* Verification Timeout */}
                <div className="flex items-start gap-4">
                  <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0 pt-2`}>
                    {t("preferences.verification-timeout", "Verification Timeout (seconds)")}
                  </label>
                  {isEditingMode ? (
                    <div className="flex-1">
                      <CustomInput
                        type="text"
                        value={formatNumberWithCommas(editFormData?.verificationTimeout)}
                        onChange={(e) => {
                          const rawValue = unformatNumber(e.target.value);

                          if (rawValue === "" || !isNaN(Number(rawValue))) {
                            handleInputChange("verificationTimeout", rawValue);
                          } else {
                            handleInputChange("verificationTimeout", "");
                          }
                        }}
                        placeholder="18000"
                        className={`w-full ${validationErrors.verificationTimeout ? colors.borderDangerOutLineButton : ""}`}
                      />
                      <div className="h-4 mt-1">
                        {validationErrors.verificationTimeout && (
                          <p className={`${colors.textDanger} text-xs`}>
                            {validationErrors.verificationTimeout}
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                      {formatNumberWithCommas(preferencesData.verificationTimeout?.codeValue) || "---"}
                    </p>
                  )}
                </div>

                {/* Use Preview */}
                <div className="flex items-center gap-4">
                  <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                    {t("preferences.use-preview", "Use Preview")}
                  </label>
                  {isEditingMode ? (
                    <ToggleSwitch
                      checked={editFormData.usePreview === "Y"}
                      onChange={() => handleInputChange("usePreview", editFormData.usePreview === "Y" ? "N" : "Y")}
                    />
                  ) : (
                    <div className="flex items-center min-h-[2.5rem] flex-1">
                      <span
                        className={`px-2 py-1 rounded-md text-sm font-bold uppercase ${preferencesData.usePreview?.codeValue == "Y"
                          ? colors.badgeYes
                          : colors.badgeNo
                          }`}
                      >
                        {preferencesData.usePreview?.codeValue == "Y" ? t("common.yes") : t("common.no")}
                      </span>
                    </div>
                  )}
                </div>

                {/* Use VEX */}
                <div className="flex items-center gap-4">
                  <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                    {t("preferences.use-vex", "Use VEX")}
                  </label>
                  {isEditingMode ? (
                    <ToggleSwitch
                      checked={editFormData.useVex === "Y"}
                      onChange={() => handleInputChange("useVex", editFormData.useVex === "Y" ? "N" : "Y")}
                      data-testid="preferences-use-vex-toggle"
                    />
                  ) : (
                    <div className="flex items-center min-h-[2.5rem] flex-1">
                      <span
                        className={`px-2 py-1 rounded-md text-sm font-bold uppercase ${preferencesData.useVex?.codeValue == "Y"
                          ? colors.badgeYes
                          : colors.badgeNo
                          }`}
                      >
                        {preferencesData.useVex?.codeValue == "Y" ? t("common.yes") : t("common.no")}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const nexusSettingsSection = () => {
    return (
      <div className={`mb-4 rounded-2xl p-4 ${colors.bgPrimary} ${colors.borderPrimary}`}>
        {/* Content with Left and Right Sections */}
        <div className="flex flex-row h-full gap-6">
          {/* Left Side - Icon, Title, Description */}
          <div className="w-1/5 flex-shrink-0 h-full p-4">
            <div className="flex flex-col items-start gap-3 h-full justify-start relative">
              <ServerSettingIcon size={48} className={colors.textSecondary} />
              <div>
                <h3 className={`font-medium ${colors.textPrimary} uppercase`}>
                  {t("preferences.nexus-settings-title", "NEXUS SETTINGS")}
                </h3>
                <p className={`text-xs ${colors.textSecondary} mt-1`}>
                  {t("preferences.nexus-settings-description", "Settings for Nexus repository integration and component agent")}
                </p>
              </div>
            </div>
          </div>

          {/* Vertical Divider */}
          <div className={`block border-l -my-4 ${colors.dividerDashed}`}></div>

          {/* Right Side - Settings */}
          <div className="w-4/5 h-full">
            <div className="space-y-4">
              {/* Row 1 */}
              <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                {/* Component Agent URL */}
                <div className="flex items-center gap-4">
                  <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                    {t("preferences.component-agent-url", "Component Agent Url")}
                  </label>
                  {isEditingMode ? (
                    <div className="flex-1">
                      <CustomInput
                        type="text"
                        value={editFormData.componentAgentUrl || ""}
                        onChange={(e) => handleInputChange("componentAgentUrl", e.target.value)}
                        placeholder=""
                        className={`flex-1 ${!urlValidations.isComponentAgentUrlValid ? colors.borderDangerOutLineButton : ""}`}
                      />
                      {!urlValidations.isComponentAgentUrlValid && (
                        <p className={`mt-1 text-xs ${colors.textDanger}`}>
                          {t("preferences.api-url-error", "Please enter a valid URL")}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                      {preferencesData.componentAgentUrl?.codeValue || "---"}
                    </p>
                  )}
                </div>
                {/* Use Import Component */}
                <div className="flex items-center gap-4">
                  <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                    {t("preferences.use-import-component", "Use Import Component")}
                  </label>
                  {isEditingMode ? (
                    <ToggleSwitch
                      checked={editFormData.useImportComponent === "Y"}
                      onChange={() =>
                        handleInputChange("useImportComponent", editFormData.useImportComponent === "Y" ? "N" : "Y")
                      }
                    />
                  ) : (
                    <div className="flex items-center min-h-[2.5rem] flex-1">
                      <span
                        className={`px-2 py-1 rounded-md text-sm font-bold uppercase ${preferencesData.useImportComponent?.codeValue == "Y"
                          ? colors.badgeYes
                          : colors.badgeNo
                          }`}
                      >
                        {preferencesData.useImportComponent?.codeValue == "Y" ? t("common.yes") : t("common.no")}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const fossidImportSettingsSection = () => {
    return (
      <div className={`mb-4 rounded-2xl p-4 ${colors.bgPrimary} ${colors.borderPrimary}`}>
        {/* Content with Left and Right Sections */}
        <div className="flex flex-row h-full gap-6">
          {/* Left Side - Icon, Title, Description */}
          <div className="w-1/5 flex-shrink-0 h-full p-4">
            <div className="flex flex-col items-start gap-3 h-full justify-start relative">
              <ImportSettingIcon size={48} className={colors.textSecondary} />
              <div>
                <h3 className={`font-medium ${colors.textPrimary} uppercase`}>
                  {t("preferences.fossid-import-settings-title", "FOSSID IMPORT SETTINGS")}
                </h3>
                <p className={`text-xs ${colors.textSecondary} mt-1`}>
                  {t("preferences.foss-id-import-settings-description", "Settings for importing data from FossID")}
                </p>
              </div>
            </div>
          </div>

          {/* Vertical Divider */}
          <div className={`block border-l -my-4 ${colors.dividerDashed}`}></div>

          {/* Right Side - Settings */}
          <div className="w-4/5 h-full">
            <div className="space-y-4">
              {/* Row 1 */}
              <div className="grid grid-cols-2 gap-y-2 gap-x-4">

                {/* Asset Classification */}
                <div className="flex items-center gap-4">
                  <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                    {t("preferences.asset-classification", "Asset Classification")}
                  </label>
                  {isEditingMode ? (
                    <CustomSelect
                      options={assetClassificationsList}
                      value={assetClassificationsList.find((opt) => opt.value == editFormData.assetClassificationSeq)}
                      onChange={(opt) => handleInputChange("assetClassificationSeq", opt?.value || null)}
                      placeholder={t("common.please-select")}
                      className="flex-1"
                      size="md"
                    />
                  ) : (
                    <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                      {assetClassificationsList.find(
                        (opt) => opt.value == preferencesData.assetClassificationSeq?.codeValue,
                      )?.label ||
                        preferencesData.assetClassificationSeq?.codeValue ||
                        "---"}
                    </p>
                  )}
                </div>


                {/* License Policy */}
                <div className="flex items-center gap-4">
                  <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                    {t("preferences.license-policy", "License Policy")}
                  </label>
                  {isEditingMode ? (
                    <CustomSelect
                      options={licensePolicyList}
                      value={licensePolicyList.find((opt) => opt.value == editFormData.licensePolicySeq)}
                      onChange={(opt) => handleInputChange("licensePolicySeq", opt?.value || null)}
                      placeholder={t("common.please-select")}
                      className="flex-2 w-full"
                      size="md"
                    />
                  ) : (
                    <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                      {licensePolicyList.find((opt) => opt.value == preferencesData.licensePolicySeq?.codeValue)
                        ?.label || "---"}
                    </p>
                  )}
                </div>

                {/* Distribution Scope */}
                <div className="flex items-center gap-4">
                  <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                    {t("preferences.distribution-scope", "Distribution Scope")}
                  </label>
                  {isEditingMode ? (
                    <CustomSelect
                      options={distributionScopeList}
                      value={distributionScopeList.find((opt) => opt.value === editFormData.distributionScopeCd)}
                      onChange={(opt) => handleInputChange("distributionScopeCd", opt?.value || null)}
                      placeholder={t("common.please-select")}
                      className="flex-1"
                      size="md"
                    />
                  ) : (
                    <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                      {distributionScopeList.find((opt) => opt.value === preferencesData.distributionScopeCd?.codeValue)
                        ?.label ||
                        preferencesData.distributionScopeCd?.codeValue ||
                        "---"}
                    </p>
                  )}
                </div>

                {/* License */}
                <div className="flex items-center gap-4">
                  <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                    {t("preferences.license", "License")}
                  </label>
                  {isEditingMode ? (
                    <div className="relative flex-1">
                      <input
                        type="text"
                        placeholder={t("preferences.license")}
                        value={editFormData.licenseName || ""}
                        readOnly
                        onClick={() => setModalSelectLicenseOpen(true)}
                        title={editFormData.licenseName || ""}
                        className={`h-10 w-full cursor-pointer rounded-lg border truncate ps-3 pr-10 py-2 text-sm font-medium focus:outline-none ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                      />
                      <MagnifyingGlassIcon
                        className={`pointer-events-none absolute top-3 right-4 h-4 w-4 ${colors.textActive}`}
                      />
                    </div>
                  ) : (
                    <p className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}>
                      {preferencesData.licenseName?.codeValue || "---"}
                    </p>
                  )}
                </div>
                {/* Import Data - Full width on small screens, spans 2 columns on larger screens */}
                {!isEditingMode && (
                  <div className="col-span-2 flex items-center gap-4">
                    <div className="flex items-center gap-1 w-36 flex-shrink-0">
                      <label className={`text-sm font-medium ${colors.textSecondary}`}>
                        {t("preferences.import-data", "Import Data:")}
                      </label>
                    </div>
                    <div className="flex items-center gap-3 flex-1">
                      <PrimaryButton
                        onClick={() => setShowImportProjectModal(true)}
                        disabled={isImportProjectsDisabled() || isImportProjectLoading}
                        className="w-auto flex-shrink-0 relative"
                      >
                        {isImportProjectLoading && <Spin className="mr-2" />}
                        {isImportProjectLoading ? t("preferences.importing-projects") : t("common.import-button")}
                      </PrimaryButton>
                      <OutlineButton
                        onClick={() => setShowImportHistoryModal(true)}
                        className="w-auto flex-shrink-0 relative ml-1"
                      >
                        {t("preferences.import-history")}
                      </OutlineButton>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <LicenseSelectionModal
          isOpen={modalSelectLicenseOpen}
          onClose={() => setModalSelectLicenseOpen(false)}
          onSelect={(selectedLicense) => handleInputChange("licenseName", selectedLicense.licenseName)}
          selectedLicense={editFormData.licenseName}
        />
      </div>
    );
  };

  const defaultNoticeHeader = () => {
    return (
      <div className={`mb-4 rounded-2xl p-4 ${colors.bgPrimary} ${colors.borderPrimary}`}>
        {/* Content with Left and Right Sections */}
        <div className="flex flex-row h-full gap-6">
          {/* Left Side - Icon, Title, Description */}
          <div className="w-1/5 flex-shrink-0 h-full p-4">
            <div className="flex flex-col items-start gap-3 h-full justify-start relative">
              <NoticeHeaderIcon size={48} className={colors.textSecondary} />
              <div>
                <h3 className={`font-medium ${colors.textPrimary} uppercase`}>
                  {t("preferences.notice-header-title", "NOTICE HEADER")}
                </h3>
                <p className={`text-xs ${colors.textSecondary} mt-1`}>
                  {t("preferences.notice-header-description", "Set default Notice header text from Notice Information")}
                </p>
              </div>
            </div>
          </div>

          {/* Vertical Divider */}
          <div className={`block border-l -my-4 ${colors.dividerDashed}`}></div>

          {/* Right Side - Settings */}
          <div className="w-4/5 h-full">
            <div className="space-y-4">
              {/* Row 1 */}
              <div className="">
                {/* Import Counts Max */}
                <div className="flex items-start gap-4">
                  <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                    {t("preferences.notice-header", "Notice Header")}
                  </label>
                  {isEditingMode ? (
                    <div className="flex-1">
                      <CustomTextarea
                        value={editFormData.defaultNoticeHeader || ""}
                        onChange={(e) => handleInputChange("defaultNoticeHeader", e.target.value)}
                        title={editFormData.defaultNoticeHeader || ""}
                        className={`w-full rounded-lg border text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} resize-none`}
                        rows={6}
                        maxLength={CHARACTER_LENGTH.COMMENT}
                      />
                      <p className={`text-xs ${colors.textCharacterCount} mt-1`}>{(editFormData.defaultNoticeHeader || "").length}/{CHARACTER_LENGTH.COMMENT}</p>
                    </div>
                  ) : (
                    <p className={`text-sm ${colors.textPrimary} flex-1 min-h-[10rem] max-h-[90vh] overflow-y-auto scroll-smooth whitespace-pre-line`}>
                      {preferencesData.defaultNoticeHeader?.remark || "---"}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (getPreferencesData.isPending || getDistributionScopes.isPending || getLicensePolicyList.isPending) {
    return (
      <LoadingSpinner />
    );
  }

  return (
    <div className={`min-h-full ${colors.bgSecondary} overflow-y-auto`} data-testid="preferences-list-page">
      {/* Header with Breadcrumbs */}
      <div className="pb-3 sticky top-0 bg-inherit z-10">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h1 className={`text-[28px] uppercase font-bold ${colors.textPrimary}`}>
              {t("preferences.title", "PREFERENCES")}
            </h1>
            {isEditingMode ? (
              <div className="flex space-x-3">
                <OutlineButton data-testid="preferences-list-cancel-button" onClick={handleCancelEdit}>{t("common.cancel-button")}</OutlineButton>
                <PrimaryButton
                  data-testid="preferences-list-save-button"
                  onClick={handleSaveEdit}
                  disabled={
                    savePreferencesData.isPending ||
                    Object.values(urlValidations).some(isValid => !isValid) ||
                    Object.values(validationErrors).some(error => error !== null && error !== undefined)
                  }
                >
                  {t("common.save-button")}
                </PrimaryButton>
              </div>
            ) : (
              <PrimaryButton data-testid="preferences-list-edit-button" onClick={handleEditClick}>{t("preferences.edit-preferences")}</PrimaryButton>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-6" data-testid="preferences-list-form">
        {serverSettingSection()}
        {identificationSettingSection()}
        {toolsLocaleEtcSection()}
        <GenesisSyncSection
          isEditingMode={isEditingMode}
          editFormData={editFormData}
          onInputChange={handleInputChange}
          preferencesData={preferencesData}
          genesisSyncStatus={genesisSyncStatus}
          isGenesisSyncLoading={isGenesisSyncLoading}
          menuPortalTarget={menuPortalTarget}
          onV1SyncSelect={handleV1GenesisSyncSelect}
          onV2SyncSelect={handleV2GenesisSyncSelect}
        />
        {nexusSettingsSection()}
        {fossidImportSettingsSection()}
        {defaultNoticeHeader()}
      </div>

      {/* Confirmation Modal */}
      <ConfirmActionModal
        data-testid="preferences-list-confirm-modal"
        isOpen={confirmationModal.isOpen}
        onClose={closeConfirmation}
        onConfirm={handleConfirmAction}
        title={confirmationModal.title}
        description={confirmationModal.description}
        confirmText={confirmationModal.confirmText}
        confirmButtonVariant={confirmationModal.confirmButtonVariant}
      />
      <ImportProjectSettingModal
        data-testid="preferences-list-import-project-modal"
        isOpen={showImportProjectModal}
        onClose={() => setShowImportProjectModal(false)}
        ignoreExistingProject={preferencesData.ignoreExistingProject?.codeValue}
        onImportSuccess={handleDataFromModal}
      />
      <ImportHistoryModal
        data-testid="preferences-list-import-history-modal"
        isOpen={showImportHistoryModal}
        onClose={() => setShowImportHistoryModal(false)}
      />
      <ClarityDynamicParamsModal
        isOpen={isClarityParamsModalOpen}
        onClose={() => setIsClarityParamsModalOpen(false)}
        rows={clarityDynamicParamsDraft}
        onAddRow={handleAddClarityParamRow}
        onUpdateRow={handleUpdateClarityParamRow}
        onDeleteRow={handleDeleteClarityParamRow}
        onToggleRow={handleToggleClarityParamRow}
        onApply={handleApplyClarityParams}
        isApplyPending={saveClarityScanConfig.isPending}
      />
    </div>
  );
}

